import { createClient } from 'https://cdn.jsdelivr.net/npm/@commerce-blocks/sdk@2.0.3/+esm';
const __layersModules = {};

__layersModules["./layers-shared.js"] = {};
((exports) => {



const SDK_STATE = {
  instance: null,
  marketCacheKey: '',
};

function getConfig() {
  return window.layersConfig || {};
}

function getPageType() {
  const contextPageType = window.negativeProviderContext?.pageType;
  if (contextPageType) return String(contextPageType);

  const pathname = window.location.pathname || '';
  if (pathname.indexOf('/search') === 0) return 'search';
  if (pathname.indexOf('/collections/') === 0) return 'collection';
  if (pathname.indexOf('/products/') === 0) return 'product';
  if (pathname.indexOf('/cart') === 0) return 'cart';
  return '';
}

function isDebugEnabled() {
  return Boolean(window.theme?.settings?.layersDebug);
}

function logDebug(label, payload) {
  if (!isDebugEnabled()) return;
  console.log(`[Layers] ${label}`, payload);
}

function runAsyncTask(label, handler) {
  Promise.resolve()
    .then(() => handler())
    .catch((error) => {
      console.error(`[Layers] ${label} failed:`, error);
    });
}

function getSortOrders() {
  const sortOrders = getConfig().sortOrders || [];

  return sortOrders
    .map((sortOrder, index) => ({
      code: String(sortOrder.code || '').trim(),
      label: String(sortOrder.name || sortOrder.code || '').trim(),
      scope: String(sortOrder.scope || '').trim().toLowerCase(),
      order: Number.isFinite(Number(sortOrder.order)) ? Number(sortOrder.order) : index,
    }))
    .filter((sortOrder) => sortOrder.code)
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.label.localeCompare(b.label);
    });
}

function getSortsForScope(allowedScopes = []) {
  const sorts = getSortOrders();
  if (!allowedScopes.length) return sorts;

  const allowed = allowedScopes.map((scope) => scope.toLowerCase());
  return sorts.filter((sort) => {
    if (!sort.scope) return true;
    return sort.scope
      .split(',')
      .map((scope) => scope.trim())
      .filter(Boolean)
      .some((scope) => allowed.includes(scope));
  });
}

function humanizeFacetCode(code) {
  return String(code || '')
    .split('.')
    .pop()
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getFacetDefinitions() {
  const facets = getConfig().facets || [];

  return facets
    .map((facet, index) => ({
      code: String(facet.code || '').trim(),
      label: String(facet.name || '').trim(),
      order: index,
    }))
    .filter((facet) => facet.code)
    .map((facet) => ({
      ...facet,
      label: facet.label || humanizeFacetCode(facet.code),
    }));
}

function formatFacetValueLabel(facetCode, value) {
  const text = String(value || '');

  if (/available|availability|in_stock/i.test(String(facetCode || ''))) {
    if (text === 'true') return 'In Stock';
    if (text === 'false') return 'Out of Stock';
  }

  return text.replace(/[_-]+/g, ' ').trim();
}

function isColorFacet(code) {
  return /color|colour/i.test(String(code || ''));
}

function toDomToken(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function mapFilterValue(rule, value) {
  if (!rule || typeof rule !== 'object') return value;

  const valueMap = rule.values;
  if (!valueMap || typeof valueMap !== 'object') return value;

  const valueKey = String(value);
  if (Object.prototype.hasOwnProperty.call(valueMap, valueKey)) {
    return valueMap[valueKey];
  }

  return value;
}

function mapFilterProperty(rule, fallbackProperty) {
  if (typeof rule === 'string' && rule.trim()) return rule.trim();
  if (rule && typeof rule === 'object' && String(rule.property || '').trim()) {
    return String(rule.property).trim();
  }
  return String(fallbackProperty || '').trim();
}

function mapFilterExpression(expression, filterMap) {
  if (!expression || typeof expression !== 'object') return null;

  const sourceProperty = String(expression.property || '').trim();
  if (!sourceProperty) return null;

  const rule = filterMap[sourceProperty];
  const property = mapFilterProperty(rule, sourceProperty);
  if (!property) return null;

  const rawOperator = String(expression.operator || '').trim();
  const operator = rawOperator || 'eq';
  const rawValues = Array.isArray(expression.values) ? expression.values : [];
  const values = rawValues.map((value) => mapFilterValue(rule, value));

  return { property, operator, values };
}

function normalizeObjectFilters(filters, filterMap) {
  const expressions = [];

  Object.entries(filters).forEach(([sourceProperty, sourceValue]) => {
    if (sourceValue == null) return;

    const rule = filterMap[sourceProperty];
    const property = mapFilterProperty(rule, sourceProperty);
    if (!property) return;

    if (Array.isArray(sourceValue)) {
      if (!sourceValue.length) return;
      expressions.push({
        property,
        operator: sourceValue.length > 1 ? 'in' : 'eq',
        values: sourceValue.map((value) => mapFilterValue(rule, value)),
      });
      return;
    }

    if (typeof sourceValue === 'object') {
      const minValue = Number(sourceValue.min);
      const maxValue = Number(sourceValue.max);

      if (Number.isFinite(minValue)) {
        expressions.push({ property, operator: 'gte', values: [minValue] });
      }

      if (Number.isFinite(maxValue)) {
        expressions.push({ property, operator: 'lte', values: [maxValue] });
      }

      return;
    }

    expressions.push({
      property,
      operator: 'eq',
      values: [mapFilterValue(rule, sourceValue)],
    });
  });

  if (!expressions.length) return undefined;
  return { conditional: 'AND', expressions };
}

function normalizeFiltersForSdk(filters, filterMapConfig) {
  if (!filters) return undefined;

  const filterMap = filterMapConfig && typeof filterMapConfig === 'object'
    ? filterMapConfig
    : {};

  if (Array.isArray(filters?.expressions)) {
    const expressions = filters.expressions
      .map((expression) => mapFilterExpression(expression, filterMap))
      .filter(Boolean);

    if (!expressions.length) return undefined;

    const conditional = String(filters.conditional || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND';
    return {
      conditional,
      expressions,
    };
  }

  if (typeof filters === 'object' && !Array.isArray(filters)) {
    return normalizeObjectFilters(filters, filterMap);
  }

  return filters;
}


function normalizeContextCode(value) {
  return String(value == null ? '' : value).trim().toUpperCase();
}

function getLayersRequestContext() {
  const config = getConfig();
  const configuredContext = config.context && typeof config.context === 'object' ? config.context : {};
  const configuredGeo = configuredContext.geo && typeof configuredContext.geo === 'object' ? configuredContext.geo : {};

  const country =
    normalizeContextCode(config.countryCode) ||
    normalizeContextCode(config.country) ||
    normalizeContextCode(configuredGeo.country) ||
    normalizeContextCode(window.Shopify?.country) ||
    normalizeContextCode(document.documentElement?.dataset?.countryCode) ||
    normalizeContextCode(document.documentElement?.lang?.split('-')?.[1]);

  const market =
    normalizeContextCode(config.marketCode) ||
    normalizeContextCode(config.market) ||
    normalizeContextCode(configuredContext.market) ||
    country;

  const context = {
    ...configuredContext,
    market: market || configuredContext.market || undefined,
    geo: {
      ...configuredGeo,
      country: country || configuredGeo.country || undefined,
    },
    shoppingChannel: configuredContext.shoppingChannel || 'web',
  };

  if (!context.market) delete context.market;
  if (!context.geo.country) delete context.geo.country;
  if (!Object.keys(context.geo).length) delete context.geo;

  return context;
}

function getLayersMarketCacheKey() {
  const context = getLayersRequestContext();
  return [
    context.market || '',
    context.geo?.country || '',
    getCurrencyCode() || '',
  ].join(':');
}

function clearClientCacheSafely(client) {
  if (!client || !client.cache) return;

  try {
    if (typeof client.cache.clear === 'function') {
      client.cache.clear();
      return;
    }

    if (typeof client.cache.invalidate === 'function') {
      client.cache.invalidate();
    }
  } catch (error) {
    console.warn('[Layers] Unable to clear SDK cache.', error);
  }
}

function getSdk() {
  const marketCacheKey = getLayersMarketCacheKey();
  if (SDK_STATE.instance && SDK_STATE.marketCacheKey === marketCacheKey) return SDK_STATE.instance;

  const config = getConfig();
  const publicToken = String(config.publicToken || '').trim();
  if (!publicToken) {
    throw new Error('Layers public token is missing in window.layersConfig.publicToken');
  }

  const context = getLayersRequestContext();
  const currency = getCurrencyCode();

  const sdkConfig = {
    token: publicToken,
    context,
    currency,
    restoreCache: false,
    cacheLifetime: 0,
    sorts: getSortOrders().map((sortOrder) => ({
      code: sortOrder.code,
      name: sortOrder.label,
    })),
    facets: getFacetDefinitions().map((facet) => ({
      code: facet.code,
      name: facet.label,
    })),
    filterAliases: config.filterAliases || config.filterMap || {},
    transforms: {
      filters: (filters) => normalizeFiltersForSdk(filters, config.filterAliases || config.filterMap || {}),
    },
    flags: {
      variants: true,
    },
  };

  const sdkResult = createClient(sdkConfig);

  if (sdkResult.error) {
    throw sdkResult.error;
  }

  SDK_STATE.instance = sdkResult.data;
  SDK_STATE.marketCacheKey = marketCacheKey;
  clearClientCacheSafely(SDK_STATE.instance);
  return SDK_STATE.instance;
}

function getResultProducts(data) {
  return data?.products || [];
}

function getResultFacets(data) {
  return data?.facets || {};
}

function getResultPagination(data, { page = 1, limit = 1, fallbackResults = 0 } = {}) {
  const totalResultsRaw = Number(data?.totalResults);
  const totalResults = Number.isFinite(totalResultsRaw) ? totalResultsRaw : fallbackResults;

  const totalPagesRaw = Number(data?.totalPages);
  if (Number.isFinite(totalPagesRaw) && totalPagesRaw >= 1) {
    return {
      totalResults,
      totalPages: Math.floor(totalPagesRaw),
    };
  }

  const inferredTotalPages = Math.max(1, Math.ceil(totalResults / Math.max(1, limit)));
  return {
    totalResults,
    totalPages: Math.max(inferredTotalPages, page),
  };
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getCurrencyCode() {
  return window.Shopify?.currency?.active || window.Shopify?.currency?.default || window.theme?.currency || 'USD';
}

function normalizePrice(value) {
  if (value == null || value === '') return null;

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;

  if (getConfig().pricesInCents) {
    return numericValue / 100;
  }

  return numericValue;
}

function formatMoney(value) {
  const amount = normalizePrice(value);
  if (amount == null) return '';

  const hasCents = Math.round(amount * 100) % 100 !== 0;

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: getCurrencyCode(),
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

function normalizeResourceId(value) {
  if (value == null) return '';

  const normalized = String(value).trim();
  if (!normalized) return '';

  const gidMatch = normalized.match(/\/(\d+)$/);
  if (gidMatch && gidMatch[1]) return gidMatch[1];

  return normalized;
}

function getProductId(product) {
  const normalizedNumericId = normalizeResourceId(product?.numericId);
  if (normalizedNumericId) return normalizedNumericId;
  return normalizeResourceId(product?.id);
}

function getProductTitle(product) {
  return String(product?.title || 'Untitled product').trim();
}

function getProductHandle(product) {
  return String(product?.handle || '').trim();
}

function getProductUrl(product) {
  if (product?.onlineStoreUrl) return String(product.onlineStoreUrl);

  const handle = getProductHandle(product);
  if (!handle) return '#';
  return `/products/${encodeURIComponent(handle)}`;
}

function getProductImageUrls(product) {
  const imageSources = [
    product?.images,
    product?.images_info,
    product?.media,
  ];
  const urls = [];

  imageSources.forEach((images) => {
    if (!Array.isArray(images)) return;

    images.forEach((image) => {
      const url = typeof image === 'string'
        ? image
        : image?.url || image?.src || image?.image?.url || image?.image?.src || '';
      const normalized = String(url || '').trim();
      if (normalized && !urls.includes(normalized)) urls.push(normalized);
    });
  });

  return urls;
}

function getProductImageUrl(product) {
  return getProductImageUrls(product)[0] || '';
}

const RESPONSIVE_IMAGE_WIDTHS = [480, 740, 980, 1200, 1440, 1680, 2048];
const RESPONSIVE_IMAGE_SIZES = '(min-width: 1025px) calc((100vw - 120px) / 3), (min-width: 768px) 50vw, 50vw';

function resizeShopifyImageUrl(url, width) {
  const source = String(url || '').trim();
  if (!source || !Number.isFinite(width)) return source;

  const match = source.match(/^(.+?)(\?[^?]*)?$/);
  const path = match ? match[1] : source;
  const query = match && match[2] ? match[2] : '';
  const extensionMatch = path.match(/^(.*?)(?:_(?:\d+x\d*|x\d+|\d+x))?(\.(?:jpe?g|png|webp))$/i);
  if (!extensionMatch) return source;

  return `${extensionMatch[1]}_${Math.floor(width)}x${extensionMatch[2]}${query}`;
}

function getResponsiveImageAttributes(url) {
  const source = String(url || '').trim();
  if (!source) return '';

  const resizedSource = resizeShopifyImageUrl(source, 1200);
  const supportsSrcset = /\.(?:jpe?g|png|webp)(?:\?[^?]*)?$/i.test(source);
  const srcset = supportsSrcset
    ? RESPONSIVE_IMAGE_WIDTHS.map((width) => `${resizeShopifyImageUrl(source, width)} ${width}w`).join(', ')
    : '';

  return `src="${escapeHtml(resizedSource)}"${
    srcset ? ` srcset="${escapeHtml(srcset)}" sizes="${escapeHtml(RESPONSIVE_IMAGE_SIZES)}"` : ''
  }`;
}

function renderResponsiveImage(url, alt, className, loading = 'lazy') {
  const attributes = getResponsiveImageAttributes(url);
  if (!attributes) return '';
  const classAttribute = className ? ` class="${escapeHtml(className)}"` : '';

  return `<img ${attributes} alt="${escapeHtml(alt)}"${classAttribute} loading="${escapeHtml(loading)}">`;
}

function renderProductImagePlaceholder() {
  return '<span class="layers-product-image-placeholder" aria-hidden="true"></span>';
}

function isProductAvailable(product) {
  return Boolean(product?.availableForSale);
}

function getMoneyAmount(value) {
  if (value == null || value === '') return null;

  if (typeof value === 'number' || typeof value === 'string') return value;

  if (typeof value === 'object') {
    if (value.amount != null) return value.amount;
    if (value.value != null) return value.value;
    if (value.price != null) return getMoneyAmount(value.price);
  }

  return null;
}

function getSelectedOrFirstVariant(product) {
  if (!product || typeof product !== 'object') return null;

  if (product.selectedOrFirstAvailableVariant) return product.selectedOrFirstAvailableVariant;
  if (product.selected_or_first_available_variant) return product.selected_or_first_available_variant;
  if (product.firstOrMatchedVariant) return product.firstOrMatchedVariant;
  if (product.first_or_matched_variant) return product.first_or_matched_variant;
  if (product.variant) return product.variant;

  if (Array.isArray(product.variants) && product.variants.length) {
    return product.variants.find((variant) => variant?.availableForSale || variant?.available) || product.variants[0];
  }

  return null;
}

function getProductPriceValue(product) {
  const variant = getSelectedOrFirstVariant(product);
  return (
    getMoneyAmount(product?.price) ??
    getMoneyAmount(product?.selectedVariantPrice) ??
    getMoneyAmount(variant?.price) ??
    getMoneyAmount(product?.priceRange?.min) ??
    getMoneyAmount(product?.price_range?.from) ??
    null
  );
}

function getProductCompareAtPriceValue(product) {
  const variant = getSelectedOrFirstVariant(product);
  console.log('compare at price variant', variant)
  return (
    getMoneyAmount(variant?.compareAtPrice) ??
    null
  );
}

function renderPriceMarkup(product) {
  const rawPrice = getProductPriceValue(product);
  const rawCompareAt = getProductCompareAtPriceValue(product);
  const price = normalizePrice(rawPrice);
  const compareAt = normalizePrice(rawCompareAt);
  console.log('raw compare at', {compareAt,price})
  if (price == null) return '';

  if (compareAt != null && compareAt > price) {
    return `<span class="grid-link__sale_price">${escapeHtml(formatMoney(rawPrice))}</span> <s>${escapeHtml(
      formatMoney(rawCompareAt)
    )}</s>`;
  }

  return escapeHtml(formatMoney(rawPrice));
}


Object.assign(exports, { getConfig: getConfig, getPageType: getPageType, isDebugEnabled: isDebugEnabled, logDebug: logDebug, runAsyncTask: runAsyncTask, getSortOrders: getSortOrders, getSortsForScope: getSortsForScope, humanizeFacetCode: humanizeFacetCode, getFacetDefinitions: getFacetDefinitions, formatFacetValueLabel: formatFacetValueLabel, isColorFacet: isColorFacet, toDomToken: toDomToken, getLayersRequestContext: getLayersRequestContext, getSdk: getSdk, getResultProducts: getResultProducts, getResultFacets: getResultFacets, getResultPagination: getResultPagination, escapeHtml: escapeHtml, getCurrencyCode: getCurrencyCode, normalizePrice: normalizePrice, formatMoney: formatMoney, getProductId: getProductId, getProductTitle: getProductTitle, getProductHandle: getProductHandle, getProductUrl: getProductUrl, getProductImageUrls: getProductImageUrls, getProductImageUrl: getProductImageUrl, renderResponsiveImage: renderResponsiveImage, renderProductImagePlaceholder: renderProductImagePlaceholder, isProductAvailable: isProductAvailable, getProductPriceValue: getProductPriceValue, getProductCompareAtPriceValue: getProductCompareAtPriceValue, renderPriceMarkup: renderPriceMarkup });
})(__layersModules["./layers-shared.js"]);


__layersModules["./layers-autocomplete.js"] = {};
((exports) => {
const { escapeHtml, getConfig, getSdk, logDebug, runAsyncTask } = __layersModules["./layers-shared.js"];


const DEFAULT_AUTOCOMPLETE_CONFIG = {
  debounceMs: 250,
  minChars: 2,
  limit: 8,
};
const SEARCH_ID_TTL_MS = 14 * 60 * 1000;

const state = {
  boundForms: new WeakSet(),
  boundStandardAutocompleteForms: new WeakSet(),
  boundPredictiveElements: new WeakSet(),
  boundRotatingPlaceholderInputs: new WeakSet(),
  predictiveDefinitionPending: false,
  formAutocompleteUnsubscribers: new WeakMap(),
  predictiveAutocompleteUnsubscribers: new WeakMap(),
  rotatingPlaceholderTimers: new WeakMap(),
  rotatingPlaceholderIndexes: new WeakMap(),
  rotatingPlaceholderBodyObserver: null,
  rotatingPlaceholderDocumentBound: false,
  documentClickBound: false,
  searchIdsByQuery: {},
  pendingSearchIds: {},
  searchController: null,
  formAutocompletes: new WeakMap(),
  formAutocompleteContainers: new WeakMap(),
};

function normalizeQuery(query) {
  return String(query || '').trim();
}

function toQueryKey(query) {
  return normalizeQuery(query).toLowerCase();
}

function toPredictiveCacheKey(query) {
  return normalizeQuery(query).toLowerCase().replace(/\s+/g, '-');
}

function getAutocompleteConfig() {
  const config = getConfig().autocomplete || {};
  return {
    debounceMs: Number(config.debounceMs) > 0 ? Number(config.debounceMs) : DEFAULT_AUTOCOMPLETE_CONFIG.debounceMs,
    minChars: Number(config.minChars) > 0 ? Number(config.minChars) : DEFAULT_AUTOCOMPLETE_CONFIG.minChars,
    limit: Number(config.limit) > 0 ? Number(config.limit) : DEFAULT_AUTOCOMPLETE_CONFIG.limit,
  };
}

function getCachedSearchId(query) {
  const key = toQueryKey(query);
  const entry = state.searchIdsByQuery[key];
  if (!entry) return '';

  if (Date.now() - entry.updatedAt > SEARCH_ID_TTL_MS) {
    delete state.searchIdsByQuery[key];
    return '';
  }

  return entry.id;
}

function cacheSearchId(query, searchId) {
  const normalizedQuery = normalizeQuery(query);
  const normalizedSearchId = normalizeQuery(searchId);
  if (!normalizedQuery || !normalizedSearchId) return;
  state.searchIdsByQuery[toQueryKey(normalizedQuery)] = {
    id: normalizedSearchId,
    updatedAt: Date.now(),
  };
}

function getSearchController() {
  if (state.searchController) return state.searchController;
  state.searchController = getSdk().search();
  return state.searchController;
}

async function ensureSearchId(query) {
  const normalizedQuery = normalizeQuery(query);
  if (!normalizedQuery) return '';

  const cachedSearchId = getCachedSearchId(normalizedQuery);
  if (cachedSearchId) return cachedSearchId;

  const key = toQueryKey(normalizedQuery);
  if (state.pendingSearchIds[key]) {
    return state.pendingSearchIds[key];
  }

  state.pendingSearchIds[key] = getSearchController()
    .prepare({ query: normalizedQuery })
    .then((response) => {
      if (!response || response.error || !response.data) return '';
      const nextSearchId = normalizeQuery(response.data.searchId);
      cacheSearchId(normalizedQuery, nextSearchId);
      return nextSearchId;
    })
    .finally(() => {
      delete state.pendingSearchIds[key];
    });

  return state.pendingSearchIds[key];
}

function buildSearchHref(query, searchId) {
  const params = new URLSearchParams();
  params.set('q', normalizeQuery(query));
  params.set('options[prefix]', 'last');

  const normalizedSearchId = normalizeQuery(searchId);
  if (normalizedSearchId) {
    params.set('search_id', normalizedSearchId);
  }

  return `/search?${params.toString()}`;
}

function getSearchForms() {
  return Array.from(document.querySelectorAll('form[role="search"]'));
}

function getSearchInput(form) {
  return form.querySelector('input[name="q"]');
}

function getFormQuery(form) {
  const input = getSearchInput(form);
  return normalizeQuery(input ? input.value : '');
}

function ensureSearchIdInput(form) {
  let input = form.querySelector('input[data-layers-search-id]');
  if (input) return input;

  input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'search_id';
  input.setAttribute('data-layers-search-id', '');
  form.appendChild(input);
  return input;
}

function syncFormSearchId(form) {
  const query = getFormQuery(form);
  const input = ensureSearchIdInput(form);
  input.value = getCachedSearchId(query);
}

function syncAllFormSearchIds() {
  getSearchForms().forEach((form) => {
    syncFormSearchId(form);
  });
}

function hydrateSearchIdFromLocation() {
  const params = new URLSearchParams(window.location.search);
  const query = params.get('q') || '';
  const searchId = params.get('search_id') || params.get('sid') || '';
  cacheSearchId(query, searchId);
}

function onSearchInput(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.name !== 'q') return;
  if (!target.form) return;

  const input = ensureSearchIdInput(target.form);
  input.value = getCachedSearchId(target.value);

  if (target.form.closest('predictive-search')) return;
  triggerStandardFormAutocomplete(target.form);
}

function onSearchSubmit(event) {
  if (event.defaultPrevented) return;

  const form = event.currentTarget;
  if (!(form instanceof HTMLFormElement)) return;

  const query = getFormQuery(form);
  if (!query) return;

  const predictiveRoot = form.closest('predictive-search');
  if (predictiveRoot && predictiveRoot.querySelector('[aria-selected="true"] a, button[aria-selected="true"]')) {
    return;
  }

  const searchIdInput = ensureSearchIdInput(form);
  const cachedSearchId = getCachedSearchId(query);
  if (cachedSearchId) {
    searchIdInput.value = cachedSearchId;
    return;
  }

  event.preventDefault();
  runAsyncTask('Autocomplete submit', async () => {
    let searchId = '';
    try {
      searchId = await ensureSearchId(query);
    } catch (error) {
      console.error('[Layers] Unable to prepare search ID on submit:', error);
    }
    searchIdInput.value = searchId;
    form.submit();
  });
}

function bindSearchForms() {
  getSearchForms().forEach((form) => {
    if (state.boundForms.has(form)) return;

    ensureSearchIdInput(form);
    syncFormSearchId(form);

    form.addEventListener('input', onSearchInput);
    form.addEventListener('submit', onSearchSubmit);

    if (!form.closest('predictive-search')) {
      bindStandardFormAutocomplete(form);
    }

    state.boundForms.add(form);
  });
}

function buildSuggestionQueries(matchedQueries) {
  const suggestions = [];
  const seen = {};
  const { limit } = getAutocompleteConfig();

  (matchedQueries || []).forEach((entry) => {
    if (suggestions.length >= limit) return;

    const text = normalizeQuery(typeof entry === 'string' ? entry : entry?.query_text || entry?.query || entry?.text || '');
    if (!text) return;

    const key = toQueryKey(text);
    if (seen[key]) return;
    seen[key] = true;
    suggestions.push(text);
  });

  return suggestions.slice(0, limit);
}

function renderAutocompleteMarkup(query, matchedQueries) {
  const normalizedQuery = normalizeQuery(query);
  const suggestions = buildSuggestionQueries(matchedQueries);
  const suggestionItems = suggestions
    .map((suggestion, index) => {
      const href = buildSearchHref(suggestion, getCachedSearchId(suggestion));
      return `<li id="predictive-search-option-query-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
<a href="${escapeHtml(href)}" class="predictive-search__item link link--text" tabindex="-1" data-layers-autocomplete-link data-layers-query="${escapeHtml(
        suggestion
      )}">
<p class="predictive-search__item-heading h5">${escapeHtml(suggestion)}</p>
</a>
</li>`;
    })
    .join('');

  const suggestionsMarkup = suggestionItems
    ? `<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-products">
<div class="predictive-search__result-group">
<div>
<h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Suggestions</h2>
<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
${suggestionItems}
</ul>
</div>
</div>
</div>`
    : '';

  const searchForHref = buildSearchHref(normalizedQuery, getCachedSearchId(normalizedQuery));
  const liveRegionText = suggestionItems
    ? `${suggestions.length} suggestion${suggestions.length === 1 ? '' : 's'} available.`
    : `Search for "${normalizedQuery}".`;

  return `<div id="predictive-search-results" role="listbox">
${suggestionsMarkup}
<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
<a class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false" href="${escapeHtml(
    searchForHref
  )}" data-layers-autocomplete-link data-layers-query="${escapeHtml(normalizedQuery)}">
<span data-predictive-search-search-for-text>Search for "${escapeHtml(normalizedQuery)}"</span>
</a>
</div>
</div>
<span class="hidden" data-predictive-search-live-region-count-value>${escapeHtml(liveRegionText)}</span>`;
}

function getExistingFormAutocompleteContainer(form) {
  const cached = state.formAutocompleteContainers.get(form);
  if (cached && cached.isConnected) return cached;
  return null;
}

function ensureStandardFormAutocompleteContainer(form) {
  const cached = getExistingFormAutocompleteContainer(form);
  if (cached) return cached;

  const existingFromMarkup =
    form.querySelector('[data-layers-autocomplete-results]') || form.querySelector('[data-predictive-search]');
  const container = existingFromMarkup || document.createElement('div');

  if (!existingFromMarkup) {
    container.className = 'layers-autocomplete-results';
    form.appendChild(container);
  }

  container.setAttribute('data-layers-autocomplete-results', '');
  container.setAttribute('aria-hidden', 'true');
  container.hidden = true;

  if (!container.hasAttribute('data-layers-autocomplete-bound')) {
    container.addEventListener('click', onAutocompleteClick);
    container.setAttribute('data-layers-autocomplete-bound', 'true');
  }

  state.formAutocompleteContainers.set(form, container);
  return container;
}

function setStandardFormAutocompleteOpen(form, isOpen) {
  const container = getExistingFormAutocompleteContainer(form);
  if (!container) return;

  container.hidden = !isOpen;
  container.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
  form.setAttribute('data-layers-autocomplete-open', isOpen ? 'true' : 'false');
}

function closeStandardFormAutocomplete(form) {
  setStandardFormAutocompleteOpen(form, false);
}

function closeAllStandardFormAutocomplete(exceptForm) {
  getSearchForms().forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    if (form === exceptForm) return;
    if (form.closest('predictive-search')) return;
    closeStandardFormAutocomplete(form);
  });
}

function getFormAutocompleteController(form) {
  const existing = state.formAutocompletes.get(form);
  if (existing) return existing;

  const controller = getSdk().suggest({ debounce: getAutocompleteConfig().debounceMs });
  state.formAutocompletes.set(form, controller);
  return controller;
}

function subscribeToController(controller, callback) {
  if (controller && typeof controller.subscribe === 'function') {
    return controller.subscribe(callback);
  }

  if (controller?.state && typeof controller.state.subscribe === 'function') {
    return controller.state.subscribe(callback);
  }

  callback(controller?.state?.value || {});
  return function noopUnsubscribe() {};
}

function renderStandardFormAutocompleteState(form, autocompleteState) {
  const input = getSearchInput(form);
  if (!input) return;

  const query = normalizeQuery(input.value);
  const { minChars } = getAutocompleteConfig();
  if (!query || query.length < minChars) {
    closeStandardFormAutocomplete(form);
    return;
  }

  if (autocompleteState.error) {
    closeStandardFormAutocomplete(form);
    return;
  }

  if (!autocompleteState.data) return;

  const responseQuery = normalizeQuery(autocompleteState.data.originalQuery);
  if (responseQuery && toQueryKey(responseQuery) !== toQueryKey(query)) return;

  const container = ensureStandardFormAutocompleteContainer(form);
  container.innerHTML = renderAutocompleteMarkup(query, autocompleteState.data.matchedQueries || []);
  setStandardFormAutocompleteOpen(form, true);
}

function bindStandardFormAutocomplete(form) {
  if (state.boundStandardAutocompleteForms.has(form)) return;

  const input = getSearchInput(form);
  if (!input) return;

  const controller = getFormAutocompleteController(form);
  ensureStandardFormAutocompleteContainer(form);

  if (!state.formAutocompleteUnsubscribers.has(form)) {
    const unsubscribe = subscribeToController(controller, (nextState) => {
      renderStandardFormAutocompleteState(form, nextState || controller.state?.value || {});
    });
    state.formAutocompleteUnsubscribers.set(form, unsubscribe);
  }

  form.addEventListener('focusin', onSearchFocusIn);
  form.addEventListener('focusout', onSearchFocusOut);
  state.boundStandardAutocompleteForms.add(form);
}

function triggerStandardFormAutocomplete(form) {
  const input = getSearchInput(form);
  if (!input) return;

  const query = normalizeQuery(input.value);
  const { minChars } = getAutocompleteConfig();
  if (!query || query.length < minChars) {
    closeStandardFormAutocomplete(form);
    return;
  }

  closeAllStandardFormAutocomplete(form);
  const controller = getFormAutocompleteController(form);

  runAsyncTask('Autocomplete prepare + execute', async () => {
    await runPrepareAndAutocomplete(query, controller, form);
  });
}

function onSearchFocusIn(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.name !== 'q') return;
  if (!target.form) return;
  if (target.form.closest('predictive-search')) return;

  if (normalizeQuery(target.value).length < getAutocompleteConfig().minChars) return;
  triggerStandardFormAutocomplete(target.form);
}

function onSearchFocusOut(event) {
  const form = event.currentTarget;
  if (!(form instanceof HTMLFormElement)) return;

  window.setTimeout(() => {
    const activeElement = document.activeElement;
    const container = getExistingFormAutocompleteContainer(form);
    if (form.contains(activeElement)) return;
    if (container && container.contains(activeElement)) return;
    closeStandardFormAutocomplete(form);
  }, 0);
}

function onDocumentClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  getSearchForms().forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    if (form.closest('predictive-search')) return;

    const container = getExistingFormAutocompleteContainer(form);
    if (form.contains(target)) return;
    if (container && container.contains(target)) return;
    closeStandardFormAutocomplete(form);
  });
}

function bindDocumentClickListener() {
  if (state.documentClickBound) return;
  document.addEventListener('click', onDocumentClick);
  state.documentClickBound = true;
}

function onAutocompleteClick(event) {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const link = target.closest('[data-layers-autocomplete-link]');
  if (!link) return;

  event.preventDefault();
  const query = normalizeQuery(link.getAttribute('data-layers-query'));
  if (!query) return;

  runAsyncTask('Autocomplete navigate', async () => {
    let searchId = '';
    try {
      searchId = await ensureSearchId(query);
    } catch (error) {
      console.error('[Layers] Unable to prepare search ID for autocomplete click:', error);
    }
    window.location.assign(buildSearchHref(query, searchId));
  });
}

function renderPredictiveState(element, autocompleteState) {
  if (autocompleteState.error) {
    element.close();
    return;
  }

  if (!autocompleteState.data) return;

  const responseQuery = normalizeQuery(autocompleteState.data.originalQuery);
  const activeQuery = normalizeQuery(element.getQuery());
  if (responseQuery && activeQuery && toQueryKey(responseQuery) !== toQueryKey(activeQuery)) {
    return;
  }

  const query = responseQuery || activeQuery;
  if (!query) return;

  const markup = renderAutocompleteMarkup(query, autocompleteState.data.matchedQueries || []);
  element.cachedResults[toPredictiveCacheKey(query)] = markup;
  element.renderSearchResults(markup);
}

async function runPrepareAndAutocomplete(query, autocomplete, form) {
  autocomplete.execute(query);

  try {
    await ensureSearchId(query);
  } catch (error) {
    console.error('[Layers] Unable to prepare search ID during autocomplete:', error);
  }

  if (form) {
    syncFormSearchId(form);
  }
  syncAllFormSearchIds();
}

function mountPredictiveElement(element) {
  if (state.boundPredictiveElements.has(element)) return;

  const { debounceMs, minChars } = getAutocompleteConfig();
  const autocomplete = getSdk().suggest({ debounce: debounceMs });
  if (!state.predictiveAutocompleteUnsubscribers.has(element)) {
    const unsubscribe = subscribeToController(autocomplete, (nextState) => {
      renderPredictiveState(element, nextState || autocomplete.state?.value || {});
    });
    state.predictiveAutocompleteUnsubscribers.set(element, unsubscribe);
  }

  element.getSearchResults = function getLayersSearchResults(searchTerm) {
    const query = normalizeQuery(searchTerm);
    if (!query || query.length < minChars) {
      this.close();
      return;
    }

    const cacheKey = toPredictiveCacheKey(query);
    const cachedMarkup = this.cachedResults[cacheKey];
    if (cachedMarkup) {
      this.renderSearchResults(cachedMarkup);
      return;
    }

    this.setLiveRegionLoadingState();
    runAsyncTask('Autocomplete prepare + execute', async () => {
      await runPrepareAndAutocomplete(query, autocomplete, this.input ? this.input.form : null);
    });
  };

  element.addEventListener('click', onAutocompleteClick);
  state.boundPredictiveElements.add(element);
}

function mountPredictiveElements() {
  const elements = Array.from(document.querySelectorAll('predictive-search'));
  if (!elements.length) return;

  if (customElements.get('predictive-search')) {
    elements.forEach((element) => {
      mountPredictiveElement(element);
    });
    return;
  }

  if (state.predictiveDefinitionPending) return;
  state.predictiveDefinitionPending = true;

  customElements
    .whenDefined('predictive-search')
    .then(() => {
      state.predictiveDefinitionPending = false;
      Array.from(document.querySelectorAll('predictive-search')).forEach((element) => {
        mountPredictiveElement(element);
      });
    })
    .catch(() => {
      state.predictiveDefinitionPending = false;
    });
}

function getRotatingPlaceholderValues(input) {
  if (!(input instanceof HTMLInputElement)) return [];

  try {
    const values = JSON.parse(input.getAttribute('data-placeholder-rotations') || '[]');
    if (!Array.isArray(values)) return [];

    return values
      .map((value) => normalizeQuery(value))
      .filter(Boolean)
      .filter((value, index, source) => source.indexOf(value) === index);
  } catch (error) {
    console.warn('[Layers] Unable to parse search placeholder rotations.', error);
    return [];
  }
}

function getRotatingPlaceholderInterval(input) {
  const interval = Number(input.getAttribute('data-placeholder-interval'));
  return Number.isFinite(interval) && interval >= 2000 ? interval : 3000;
}

function isSearchOverlayOpen(input) {
  const details = input.closest('details');
  return Boolean(document.body.classList.contains('show-search') || details?.open);
}

function setRotatingPlaceholder(input, values, index) {
  if (!values.length || normalizeQuery(input.value)) return;
  const nextIndex = ((index % values.length) + values.length) % values.length;
  state.rotatingPlaceholderIndexes.set(input, nextIndex);
  input.setAttribute('placeholder', values[nextIndex]);
}

function stopRotatingPlaceholder(input) {
  const timer = state.rotatingPlaceholderTimers.get(input);
  if (!timer) return;
  window.clearInterval(timer);
  state.rotatingPlaceholderTimers.delete(input);
}

function startRotatingPlaceholder(input) {
  const values = getRotatingPlaceholderValues(input);
  if (!values.length) return;

  const currentIndex = state.rotatingPlaceholderIndexes.get(input) || 0;
  setRotatingPlaceholder(input, values, currentIndex);

  if (values.length === 1 || state.rotatingPlaceholderTimers.has(input)) return;

  const timer = window.setInterval(() => {
    if (!isSearchOverlayOpen(input)) {
      stopRotatingPlaceholder(input);
      return;
    }

    if (normalizeQuery(input.value)) return;

    const activeIndex = state.rotatingPlaceholderIndexes.get(input) || 0;
    setRotatingPlaceholder(input, values, activeIndex + 1);
  }, getRotatingPlaceholderInterval(input));

  state.rotatingPlaceholderTimers.set(input, timer);
}

function syncRotatingPlaceholder(input) {
  if (!getRotatingPlaceholderValues(input).length) return;

  if (isSearchOverlayOpen(input)) {
    startRotatingPlaceholder(input);
    return;
  }

  stopRotatingPlaceholder(input);
}

function syncAllRotatingPlaceholders() {
  Array.from(document.querySelectorAll('input[data-placeholder-rotations]')).forEach((input) => {
    if (input instanceof HTMLInputElement) {
      syncRotatingPlaceholder(input);
    }
  });
}

function bindRotatingPlaceholder(input) {
  if (state.boundRotatingPlaceholderInputs.has(input)) return;
  if (!getRotatingPlaceholderValues(input).length) return;

  input.addEventListener('input', () => {
    syncRotatingPlaceholder(input);
  });

  const details = input.closest('details');
  if (details) {
    details.addEventListener('toggle', () => {
      syncRotatingPlaceholder(input);
    });
  }

  state.boundRotatingPlaceholderInputs.add(input);
  syncRotatingPlaceholder(input);
}

function initRotatingPlaceholders() {
  Array.from(document.querySelectorAll('input[data-placeholder-rotations]')).forEach((input) => {
    if (input instanceof HTMLInputElement) {
      bindRotatingPlaceholder(input);
    }
  });

  if (!state.rotatingPlaceholderBodyObserver) {
    state.rotatingPlaceholderBodyObserver = new MutationObserver(syncAllRotatingPlaceholders);
    state.rotatingPlaceholderBodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  if (!state.rotatingPlaceholderDocumentBound) {
    document.addEventListener('toggle', syncAllRotatingPlaceholders, true);
    state.rotatingPlaceholderDocumentBound = true;
  }
}

async function mountAutocomplete() {
  hydrateSearchIdFromLocation();
  bindSearchForms();
  bindDocumentClickListener();
  mountPredictiveElements();
  initRotatingPlaceholders();

  const forms = getSearchForms();
  logDebug('Autocomplete mounted', {
    forms: forms.length,
    standardForms: forms.filter((form) => !form.closest('predictive-search')).length,
    predictive: document.querySelectorAll('predictive-search').length,
  });
}




Object.assign(exports, { mountAutocomplete: mountAutocomplete });
})(__layersModules["./layers-autocomplete.js"]);


__layersModules["./layers-collection-search.js"] = {};
((exports) => {
const { getConfig, formatFacetValueLabel, getFacetDefinitions, getPageType, getResultFacets, getResultPagination, getResultProducts, getSdk, getSortsForScope, humanizeFacetCode, isColorFacet, logDebug, runAsyncTask, toDomToken, escapeHtml, getProductId, getProductHandle, getProductTitle, getProductUrl, getProductImageUrls, renderResponsiveImage, renderProductImagePlaceholder, isProductAvailable, renderPriceMarkup } = __layersModules["./layers-shared.js"];


const DEFAULT_PAGE_LIMIT = 60;
const DEFAULT_PAGINATION_MODE = 'default';
const MAX_FACET_VALUES = 50;
const MIN_SCROLL_TRACKED_PRODUCT_POSITION = 10;
const PRODUCT_SKELETON_COUNT = DEFAULT_PAGE_LIMIT;
const SIZE_HEADER_LABELS = {
  band: 'Non-Wire Bras',
  size: 'Apparel + Underwear',
};
const SIZE_VALUE_ORDER = [
  '00',
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  'xxs',
  'xs',
  'xs-s',
  's',
  'm',
  'm-l',
  'm-xl',
  'l',
  'xl',
  'xl-xxl',
  'xxl',
  'os',
];
const MANUAL_ORDER_FACET_TOKENS = ['bras', 'mixed', 'underwear', 'thongs'];
const FACET_HEADER_GROUPS = {
  braTops: {
    id: 'bra-tops',
    beforeId: 'bras',
    beforeLabel: 'Bras',
    label: 'Bra Tops',
    optionLabels: ['XS', 'S', 'M', 'L'],
  },
  everythingElse: {
    id: 'everything-else',
    beforeId: 'everything-else-before',
    beforeLabel: 'Everything Else',
    label: 'Everything Else',
    optionLabels: ['XS-M', 'XS', 'S', 'M', 'M-XL', 'L'],
  },
  cottonUnderwear: {
    id: 'cotton-underwear',
    beforeId: 'non-cotton-underwear',
    beforeLabel: 'Underwear',
    label: 'Cotton Underwear',
    optionLabels: ['XS-M', 'M-XL'],
    facetTokens: ['underwear', 'thongs'],
  },
};

const SWATCH_TOKEN_ALIASES = {
  'off-white': ['offwhite'],
  offwhite: ['off-white'],
  'black-off-white': ['black-offwhite'],
  'black-offwhite': ['black-off-white'],
  'off-white-black': ['offwhite-black'],
  'offwhite-black': ['off-white-black'],
  'buff-off-white': ['buff-offwhite'],
  'buff-offwhite': ['buff-off-white'],
  'buff-black': ['buff-blacktrim'],
  'buff-blacktrim': ['buff-black'],
  'haze-black': ['haze-blacktrim'],
  'haze-blacktrim': ['haze-black'],
  'peach-black': ['peach-blacktrim'],
  'peach-blacktrim': ['peach-black'],
  'white-feel': ['white-feel-good'],
  'ice-blue': ['ice'],
  grey: ['heather'],
  gray: ['heather'],
  'heather-grey-wht': ['heather-grey-white'],
  'navy-wht': ['navy-white'],
  'coral-wht': ['coral-white'],
  'moon-blk': ['moon-black'],
  'peach-blk': ['peach-black', 'peach-blacktrim'],
  'haze-blk': ['haze-black', 'haze-blacktrim'],
  'black-white-heather-grey': ['black-white-grey'],
};

const SCOPES = {
  collection: ['collection', 'browse'],
  search: ['search', 'text_search'],
};

const state = {
  requestId: 0,
  lastQueryKey: '',
  searchIdsByQuery: {},
  lastPageMeta: {
    currentPage: 1,
    totalPages: 1,
  },
  mobileFiltersOpen: false,
  facetValueRegistry: {},
  openFacetCodes: {},
  infiniteObserver: null,
  isInfiniteLoading: false,
  backToTopTicking: false,
  scrollPositionUpdateTimer: null,
  isRestoringScrollPosition: false,
};

let listenersBound = false;

function getProductPositionForPageIndex(pageNumber, index) {
  const page = Number.isFinite(Number(pageNumber)) && Number(pageNumber) > 0
    ? Math.floor(Number(pageNumber))
    : 1;
  const positionIndex = Number.isFinite(Number(index)) && Number(index) >= 0
    ? Math.floor(Number(index))
    : 0;
  const limit = getPageLimit(getPageType());

  return ((page - 1) * limit) + positionIndex + 1;
}

function renderProductGridCards(products, pageNumber) {
  return products
    .map((product, index) => renderProductGridCard(product, pageNumber, getProductPositionForPageIndex(pageNumber, index)))
    .join('');
}

function prefersReducedViewMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

function runLayersViewTransition(callback) {
  if (
    typeof callback !== 'function' ||
    typeof document.startViewTransition !== 'function' ||
    prefersReducedViewMotion()
  ) {
    callback();
    return;
  }

  document.documentElement.classList.add('layers-view-transition-active');
  let didRunCallback = false;

  try {
    const transition = document.startViewTransition(() => {
      didRunCallback = true;
      callback();
    });

    transition.finished.finally(() => {
      document.documentElement.classList.remove('layers-view-transition-active');
    });
  } catch (error) {
    document.documentElement.classList.remove('layers-view-transition-active');
    if (!didRunCallback) {
      callback();
    }
  }
}

function scheduleLayersRender(callback, useViewTransition = false) {
  return new Promise((resolve) => {
    const runRender = () => {
      if (useViewTransition) {
        runLayersViewTransition(() => {
          callback();
          resolve();
        });
        return;
      }

      callback();
      resolve();
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(runRender);
    } else {
      window.setTimeout(runRender, 0);
    }
  });
}

function getCollectionHandle() {
  const segments = String(window.location.pathname || '')
    .match(/\/collections\/([^/?#]+)/g);

  if (!segments || !segments.length) {
    return '';
  }

  const last = segments[segments.length - 1];
  const match = last.match(/\/collections\/([^/?#]+)/);

  return match && match[1]
    ? decodeURIComponent(match[1])
    : '';
}

function getSearchQuery() {
  return new URLSearchParams(window.location.search).get('q') || '';
}

function getSearchId() {
  const params = new URLSearchParams(window.location.search);
  return params.get('search_id') || params.get('sid') || '';
}

function getCurrentPage() {
  const params = new URLSearchParams(window.location.search);
  const page = Number(params.get('page') || 0);
  if (Number.isFinite(page) && page > 0) return Math.floor(page);

  const productPosition = Number(params.get('product_position') || 0);
  if (Number.isFinite(productPosition) && productPosition > 0) {
    return Math.max(1, Math.ceil(productPosition / getPageLimit(getPageType())));
  }

  return 1;
}

function getCurrentProductPosition() {
  const position = Number(new URLSearchParams(window.location.search).get('product_position') || 0);
  return Number.isFinite(position) && position >= MIN_SCROLL_TRACKED_PRODUCT_POSITION ? Math.floor(position) : 0;
}

function getPageLimit(pageType) {
  const config = getConfig();
  const pageConfig = config[pageType] || {};

  const limit = Number(pageConfig.limit || config.pageSize || config.defaultPageSize || DEFAULT_PAGE_LIMIT);
  return Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : DEFAULT_PAGE_LIMIT;
}

function updateHistorySearchParams(mutator) {
  const nextUrl = new URL(window.location.href);
  mutator(nextUrl.searchParams);
  window.history.pushState({ layers: true }, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
}

function replaceHistorySearchParams(mutator) {
  const nextUrl = new URL(window.location.href);
  mutator(nextUrl.searchParams);
  window.history.replaceState(window.history.state, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
}

function buildHrefForPage(page) {
  const nextUrl = new URL(window.location.href);
  if (page > 1) {
    nextUrl.searchParams.set('page', String(page));
  } else {
    nextUrl.searchParams.delete('page');
  }
  nextUrl.searchParams.delete('product_position');

  return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
}

async function ensureSearchId(query, urlSearchId) {
  const normalizedQuery = String(query || '').trim();
  if (!normalizedQuery) return '';

  if (urlSearchId) {
    state.searchIdsByQuery[normalizedQuery] = urlSearchId;
    return urlSearchId;
  }

  const cachedSearchId = state.searchIdsByQuery[normalizedQuery];
  if (cachedSearchId) {
    replaceHistorySearchParams((params) => {
      params.set('search_id', cachedSearchId);
    });
    return cachedSearchId;
  }

  const sdk = getSdk();
  const prepareResult = await sdk.search().prepare({ query: normalizedQuery });
  if (prepareResult.error) {
    console.error('[Layers] Search prepare failed:', prepareResult.error);
    return '';
  }

  const preparedSearchId = String(prepareResult.data.searchId || '').trim();
  if (!preparedSearchId) return '';

  state.searchIdsByQuery[normalizedQuery] = preparedSearchId;
  replaceHistorySearchParams((params) => {
    params.set('search_id', preparedSearchId);
  });

  return preparedSearchId;
}

function getSortCodeFromLocation() {
  const config = getConfig();
  const params = new URLSearchParams(window.location.search);
  const rawSort = params.get('sort') || params.get('sort_by') || '';
  if (!rawSort) return '';

  const sortMap = config.sortMap || {};
  return sortMap[rawSort] || rawSort;
}

function resolveSortCode(allowedScopes) {
  const config = getConfig();
  const sorts = getSortsForScope(allowedScopes);
  const selectedSort = getSortCodeFromLocation();
  const defaultSort = config.defaultSortOrder || (sorts[0] && sorts[0].code) || '';

  if (!selectedSort) return defaultSort;
  if (!sorts.length) return selectedSort;
  if (sorts.some((sort) => sort.code === selectedSort)) return selectedSort;
  return defaultSort;
}

function parseFilterStateFromParams(params) {
  const listFilters = {};
  const rangeFilters = {};

  for (const [key, rawValue] of params.entries()) {
    if (!rawValue) continue;

    const rangeMatch = key.match(/^filters\[(.+)\]\[(min|max)\]$/);
    if (rangeMatch) {
      const property = rangeMatch[1];
      const bound = rangeMatch[2];
      const value = Number(rawValue);
      if (!Number.isFinite(value)) continue;

      if (!rangeFilters[property]) rangeFilters[property] = {};
      rangeFilters[property][bound] = value;
      continue;
    }

    const listMatch = key.match(/^filters\[(.+)\]$/);
    if (!listMatch) continue;

    const property = listMatch[1];
    if (!listFilters[property]) listFilters[property] = [];
    if (!listFilters[property].includes(rawValue)) {
      listFilters[property].push(rawValue);
    }
  }

  return { listFilters, rangeFilters };
}

function buildFilterGroup(listFilters, rangeFilters) {
  const expressions = [];

  Object.entries(listFilters).forEach(([property, values]) => {
    if (!values.length) return;
    expressions.push({
      property,
      operator: values.length > 1 ? 'in' : 'eq',
      values,
    });
  });

  Object.entries(rangeFilters).forEach(([property, range]) => {
    if (Number.isFinite(range.min)) {
      expressions.push({ property, operator: 'gte', values: [range.min] });
    }

    if (Number.isFinite(range.max)) {
      expressions.push({ property, operator: 'lte', values: [range.max] });
    }
  });

  if (!expressions.length) return undefined;
  return { conditional: 'AND', expressions };
}

function getActiveFilterStateFromLocation() {
  return parseFilterStateFromParams(new URLSearchParams(window.location.search));
}

function getRegisteredFacetValues(code) {
  return Array.isArray(state.facetValueRegistry[code])
    ? state.facetValueRegistry[code]
    : [];
}

function registerFacetValues(code, values) {
  const existingValues = getRegisteredFacetValues(code);
  const nextValues = [...existingValues, ...values]
    .filter((value) => value && !isNullFacetValue(value))
    .filter((value, index, source) => source.indexOf(value) === index);

  state.facetValueRegistry[code] = nextValues;
  return nextValues;
}

function getFiltersFromLocation() {
  const { listFilters, rangeFilters } = getActiveFilterStateFromLocation();
  return buildFilterGroup(listFilters, rangeFilters);
}

function getAllActiveFilterCount(activeFilters) {
  let count = 0;

  Object.values(activeFilters.listFilters).forEach((values) => {
    count += values.length;
  });

  count += Object.keys(activeFilters.rangeFilters).length;
  return count;
}

function stripFilterParamsForProperty(params, property) {
  const keysToRemove = [];

  for (const [key] of params.entries()) {
    const listMatch = key.match(/^filters\[(.+)\]$/);
    if (listMatch && listMatch[1] === property) {
      keysToRemove.push(key);
      continue;
    }

    const rangeMatch = key.match(/^filters\[(.+)\]\[(min|max)\]$/);
    if (rangeMatch && rangeMatch[1] === property) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => params.delete(key));
}

function updateLocationForFacet(property, values) {
  const nextValues = values
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .filter((value, index, source) => source.indexOf(value) === index);

  updateHistorySearchParams((params) => {
    stripFilterParamsForProperty(params, property);
    nextValues.forEach((value) => {
      params.append(`filters[${property}]`, value);
    });
    params.delete('page');
    params.delete('product_position');
  });
}

function clearAllFiltersFromLocation() {
  updateHistorySearchParams((params) => {
    const keysToRemove = [];
    for (const [key] of params.entries()) {
      if (key.indexOf('filters[') === 0) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => params.delete(key));
    params.delete('page');
    params.delete('product_position');
  });
}

function buildCollectionSearchQueryKey() {
  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  url.searchParams.delete('product_position');
  return `${url.pathname}?${url.searchParams.toString()}`;
}

function updateLocationForPage(page) {
  updateHistorySearchParams((params) => {
    if (page > 1) {
      params.set('page', String(page));
    } else {
      params.delete('page');
    }
    params.delete('product_position');
  });
}

function updateLocationForSort(sortCode) {
  updateHistorySearchParams((params) => {
    if (sortCode) {
      params.set('sort', sortCode);
    } else {
      params.delete('sort');
    }

    params.delete('sort_by');
    params.delete('page');
    params.delete('product_position');
  });
}

function updateLocationForFacetToggle(property, valueCode) {
  const activeFilters = getActiveFilterStateFromLocation();
  const values = activeFilters.listFilters[property] ? [...activeFilters.listFilters[property]] : [];
  const index = values.indexOf(valueCode);

  if (index >= 0) {
    values.splice(index, 1);
  } else {
    values.push(valueCode);
  }

  updateLocationForFacet(property, values);
}

function getPaginationMode(pageType) {
  const normalizeMode = (rawMode) => {
    const mode = String(rawMode || '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');

    if (!mode) return '';
    if (mode.indexOf('infinite') >= 0) return 'infinite';
    if (mode === 'load_more' || mode === 'loadmore') return 'load_more';
    if (mode === 'default' || mode === 'pagination' || mode === 'paginate') {
      return DEFAULT_PAGINATION_MODE;
    }

    return mode;
  };

  const getLegacyBoostPaginationMode = () => {
    const settings = window.bcSfFilterConfig?.settings;
    if (!settings || typeof settings !== 'object') return '';

    const general = settings.general;
    if (!general || typeof general !== 'object') return '';

    return general.paginationType || '';
  };

  const config = getConfig();
  const pageConfig = config[pageType] || {};
  const mode = normalizeMode(
    pageConfig.paginationMode ||
      pageConfig.pagination_mode ||
      config.paginationMode ||
      config.pagination_mode ||
      getLegacyBoostPaginationMode() ||
      DEFAULT_PAGINATION_MODE
  );

  if (mode === 'load_more') return 'load_more';
  if (mode === 'infinite') return 'infinite';
  return DEFAULT_PAGINATION_MODE;
}

function renderEmptyResults(message) {
  return `<div class="grid__item one-whole"><p>${escapeHtml(message)}</p></div>`;
}

function renderFacetTreeSkeleton({ isMobile }) {
  if (isMobile) {
    return renderMobileFacetModal(`<div class="layers-filter-tree-content" aria-busy="true">
<div class="layers-filter-accordion layers-filter-accordion--skeleton">
<div class="layers-filter-accordion__summary"><span class="layers-filter-skeleton-line layers-filter-skeleton-line--short"></span></div>
</div>
<div class="layers-filter-accordion layers-filter-accordion--skeleton">
<div class="layers-filter-accordion__summary"><span class="layers-filter-skeleton-line layers-filter-skeleton-line--medium"></span></div>
</div>
<div class="layers-filter-accordion layers-filter-accordion--skeleton">
<div class="layers-filter-accordion__summary"><span class="layers-filter-skeleton-line layers-filter-skeleton-line--short"></span></div>
</div>
</div>`);
  }

  return `<ul class="layers-filter-tree-content layers-filter-tree-content--desktop" role="list" aria-busy="true">
<li class="layers-filter-accordion layers-filter-accordion--desktop layers-filter-accordion--skeleton">
<div class="layers-filter-accordion__summary"><span class="layers-filter-skeleton-line layers-filter-skeleton-line--short"></span></div>
</li>
<li class="layers-filter-accordion layers-filter-accordion--desktop layers-filter-accordion--skeleton">
<div class="layers-filter-accordion__summary"><span class="layers-filter-skeleton-line layers-filter-skeleton-line--medium"></span></div>
</li>
<li class="layers-filter-accordion layers-filter-accordion--desktop layers-filter-accordion--skeleton">
<div class="layers-filter-accordion__summary"><span class="layers-filter-skeleton-line layers-filter-skeleton-line--short"></span></div>
</li>
</ul>`;
}

function renderFilterButtonSkeleton() {
  return '<button class="layers-filter-toggle-button layers-filter-toggle-button--loading" aria-label="Loading" disabled><span class="layers-filter-skeleton-line layers-filter-skeleton-line--short"></span></button>';
}

function renderProductGridSkeletons(count) {
  const total = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : PRODUCT_SKELETON_COUNT;
  let markup = '';

  for (let index = 0; index < total; index += 1) {
    markup += `<div class="grid-product layers-result-card layers-content-visibility-card layers-product-skeleton" data-layers-product-skeleton="true" aria-hidden="true">
<span class="grid-product__image-wrapper">
<span class="grid-product__image-link">
<span class="layers-product-image-placeholder layers-product-skeleton-image"></span>
</span>
</span>
<span class="grid-product__meta layers-product-skeleton-meta">
<span class="layers-product-skeleton-text layers-product-skeleton-text--wide"></span>
<span class="layers-product-skeleton-text layers-product-skeleton-text--short"></span>
</span>
</div>`;
  }

  return markup;
}

function getProductGridSkeletonCount(resultsContainer, fallbackCount) {
  const configuredCount = Number(resultsContainer?.dataset?.layersSkeletonCount);
  if (Number.isFinite(configuredCount) && configuredCount >= 0) {
    return Math.floor(configuredCount);
  }

  const existingCount = resultsContainer?.querySelectorAll('[data-layers-product-skeleton]').length || 0;
  if (existingCount > 0) return existingCount;

  return Number.isFinite(fallbackCount) && fallbackCount > 0 ? Math.floor(fallbackCount) : PRODUCT_SKELETON_COUNT;
}

function removeProductGridSkeletons(resultsContainer) {
  if (!resultsContainer) return;

  const skeletons = resultsContainer.querySelectorAll('[data-layers-product-skeleton]');
  skeletons.forEach((skeleton) => skeleton.remove());
}

function extractMetafieldValue(container, namespace, key) {
  if (!container) return null;

  if (Array.isArray(container)) {
    const match = container.find((metafield) => {
      if (!metafield || typeof metafield !== 'object') return false;
      return (
        String(metafield.namespace || '').trim() === namespace &&
        String(metafield.key || '').trim() === key
      );
    });
    return match ? match.value : null;
  }

  if (typeof container !== 'object') return null;

  const namespaceObject = container[namespace];
  if (
    namespaceObject &&
    typeof namespaceObject === 'object' &&
    Object.prototype.hasOwnProperty.call(namespaceObject, key)
  ) {
    return namespaceObject[key];
  }

  const dottedKey = `${namespace}.${key}`;
  if (Object.prototype.hasOwnProperty.call(container, dottedKey)) {
    return container[dottedKey];
  }

  if (Object.prototype.hasOwnProperty.call(container, key)) {
    return container[key];
  }

  return null;
}

function toTextList(value) {
  if (value == null) return [];

  if (Array.isArray(value)) {
    return value
      .flatMap((item) => toTextList(item))
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  }

  if (typeof value === 'object') {
    if (Object.prototype.hasOwnProperty.call(value, 'value')) {
      return toTextList(value.value);
    }
    if (Object.prototype.hasOwnProperty.call(value, 'values')) {
      return toTextList(value.values);
    }
    return [];
  }

  const raw = String(value || '').trim();
  if (!raw) return [];

  if ((raw[0] === '[' && raw[raw.length - 1] === ']') || (raw[0] === '{' && raw[raw.length - 1] === '}')) {
    try {
      return toTextList(JSON.parse(raw));
    } catch (error) {
      return [raw];
    }
  }

  return [raw];
}

function normalizeNumericId(value) {
  if (value == null) return '';

  const normalized = String(value).trim();
  if (!normalized) return '';

  const gidMatch = normalized.match(/\/(\d+)$/);
  if (gidMatch && gidMatch[1]) return gidMatch[1];

  return normalized;
}

function getProductNumericId(product) {
  const rawId = product?.numericId ?? product?.id;
  return normalizeNumericId(rawId);
}

function getVariantNumericId(variant) {
  const rawId = variant?.numericId ?? variant?.id;
  return normalizeNumericId(rawId);
}

function getVariantBadges(variant) {
  const metafieldValue = extractMetafieldValue(variant?.metafields, 'custom', 'variant_badges');
  return toTextList(metafieldValue);
}

function getProductBadge(product) {
  const metafieldValue = extractMetafieldValue(product?.metafields, 'custom', 'product_badge');
  const [firstValue] = toTextList(metafieldValue);
  return firstValue || '';
}

function getProductColorCount(product) {
  const colorValues = new Set();
  const addColor = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized) colorValues.add(normalized);
  };

  const optionCollection = product?.options;
  if (Array.isArray(optionCollection)) {
    optionCollection.forEach((option) => {
      if (!option || typeof option !== 'object') return;
      if (!/colou?r/i.test(String(option.name || ''))) return;

      const optionValues = option.values ?? option.value;
      if (Array.isArray(optionValues)) {
        optionValues.forEach((value) => addColor(value));
      } else {
        addColor(optionValues);
      }
    });
  } else if (optionCollection && typeof optionCollection === 'object') {
    Object.entries(optionCollection).forEach(([name, values]) => {
      if (!/colou?r/i.test(String(name || ''))) return;
      if (Array.isArray(values)) {
        values.forEach((value) => addColor(value));
        return;
      }

      if (values && typeof values === 'object' && Array.isArray(values.values)) {
        values.values.forEach((value) => addColor(value));
        return;
      }

      addColor(values);
    });
  }

  if (!colorValues.size && Array.isArray(product?.variants)) {
    product.variants.forEach((variant) => {
      const selectedOptions = variant?.selectedOptions || variant?.selected_options;
      if (!Array.isArray(selectedOptions)) return;

      selectedOptions.forEach((selectedOption) => {
        if (!selectedOption || typeof selectedOption !== 'object') return;
        if (!/colou?r/i.test(String(selectedOption.name || ''))) return;
        addColor(selectedOption.value);
      });
    });
  }

  return colorValues.size;
}

function renderProductBadges(product) {
  const productId = getProductNumericId(product) || getProductId(product);
  const productIdAttr = escapeHtml(productId);
  let markup = '';
  let firstVariantBadgeGroupRendered = false;

  if (Array.isArray(product?.variants)) {
    product.variants.forEach((variant) => {
      const badges = getVariantBadges(variant);
      if (!badges.length) return;

      const variantId = getVariantNumericId(variant);
      const variantIdAttr = escapeHtml(variantId);

      badges.forEach((badge) => {
        markup += `<div class="saletag variant-badge${
          firstVariantBadgeGroupRendered ? ' hidden' : ''
        }" data-variant-id="${variantIdAttr}" data-product-id="${productIdAttr}">${escapeHtml(
          badge
        )}</div>`;
      });

      firstVariantBadgeGroupRendered = true;
    });
  }

  const productBadge = getProductBadge(product);
  if (productBadge) {
    markup += `<div class="saletag product-badge">${escapeHtml(productBadge)}</div>`;
  }

  return markup;
}

function renderColorCount(product) {
  const colorCount = getProductColorCount(product);
  if (!Number.isFinite(colorCount) || colorCount <= 1) return '';
  return `<div class="products-in-family">${colorCount} colors</div>`;
}

function renderProductGridCard(product, pageNumber, productPosition) {
  const id = getProductId(product);
  const handle = getProductHandle(product);
  const title = getProductTitle(product);
  const url = getProductUrl(product);
  const badgesMarkup = renderProductBadges(product);
  const colorCountMarkup = renderColorCount(product);

  const imageUrls = getProductImageUrls(product);
  const primaryImageUrl = imageUrls[0] || '';

  const available = isProductAvailable(product);
  const soldOutClass = available ? '' : ' sold-out';

  const imageMarkup = primaryImageUrl
    ? renderResponsiveImage(primaryImageUrl, title, 'grid-product__image')
    : renderProductImagePlaceholder();

  return `<div data-product-id="${escapeHtml(
    id
  )}" data-id="${escapeHtml(id)}" data-product-handle="${escapeHtml(
    handle
  )}" data-layers-result-page="${escapeHtml(
    pageNumber || 1
  )}" data-layers-product-position="${escapeHtml(
    productPosition || ''
  )}" class="grid-product layers-result-card layers-content-visibility-card${soldOutClass}">
<a class="plp-a-item" href="${escapeHtml(url)}">
<span class="grid-product__image-wrapper${soldOutClass}">
${badgesMarkup}
<span class="grid-product__image-link">
${imageMarkup}
</span>
</span>
<span class="grid-product__meta">
<span class="grid-product__title">${escapeHtml(title)}</span>
<span class="grid-product__price">${renderPriceMarkup(product)}</span>
</span>
</a>
${colorCountMarkup}
</div>`;
}

function parseCollectionPosterData() {
  const dataElement = document.getElementById('collection-poster-data');
  if (!dataElement) return [];

  try {
    const data = JSON.parse(dataElement.textContent || '{}');
    const posters = Array.isArray(data.posters) ? data.posters : [];
    return posters.filter((poster) => poster && typeof poster === 'object');
  } catch (error) {
    console.warn('[Layers] Unable to parse collection poster data.', error);
    return [];
  }
}

function getCollectionPosterPosition(poster) {
  const rawPosition = window.innerWidth <= 768
    ? poster['position mobile']
    : poster.position;
  const position = Number(rawPosition);
  return Number.isFinite(position) && position > 0 ? Math.floor(position) : 0;
}

function renderCollectionPoster(poster) {
  const link = String(poster.link || '').trim();
  const image = String(poster.image || '').trim();
  const title = String(poster.title || '').trim();
  const buttonLabel = String(poster.button_label || '').trim();
  if (!image) return '';

  const imageMarkup = `<img class="collection-poster-image" src="${escapeHtml(image)}" alt="${escapeHtml(
    title
  )}" style="object-fit: cover; width: 100%; height: ${window.innerWidth <= 768 ? '100%' : 'auto'};">`;
  const linkedImageMarkup = link
    ? `<a href="${escapeHtml(link)}">${imageMarkup}</a>`
    : imageMarkup;
  const buttonMarkup = link && buttonLabel
    ? `<a href="${escapeHtml(link)}" class="link link--text" title="${escapeHtml(title)}">${escapeHtml(
        buttonLabel
      )}</a>`
    : '';

  return `<div class="grid__item grid_poster grid-product poster-wrapper layers-content-visibility-card" data-layers-collection-poster="true">
<div class="collection-poster-image-wrapper">
${linkedImageMarkup}
</div>
<div class="collection-poster-content">
<span class="collection-poster-title">${escapeHtml(title)}</span>
${buttonMarkup}
</div>
</div>`;
}

function insertCollectionPosters(resultsContainer) {
  if (!resultsContainer || getPageType() !== 'collection') return;

  resultsContainer
    .querySelectorAll('[data-layers-collection-poster="true"]')
    .forEach((posterNode) => posterNode.remove());

  const posters = parseCollectionPosterData()
    .map((poster) => ({
      poster,
      position: getCollectionPosterPosition(poster),
    }))
    .filter((entry) => entry.position > 0)
    .sort((a, b) => a.position - b.position);

  if (!posters.length) return;

  posters.forEach(({ poster, position }) => {
    const posterMarkup = renderCollectionPoster(poster);
    if (!posterMarkup) return;

    const productNodes = Array.from(
      resultsContainer.querySelectorAll(':scope > .grid-product:not([data-layers-collection-poster="true"])')
    );
    const targetNode = productNodes[position - 1] || null;
    if (targetNode) {
      targetNode.insertAdjacentHTML('afterend', posterMarkup);
      return;
    }

    resultsContainer.insertAdjacentHTML('beforeend', posterMarkup);
  });
}

function buildPageWindow(currentPage, totalPages) {
  const pages = [];
  const start = Math.max(1, currentPage - 2);
  const end = Math.min(totalPages, currentPage + 2);

  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push(null);
  }

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  if (end < totalPages) {
    if (end < totalPages - 1) pages.push(null);
    pages.push(totalPages);
  }

  return pages;
}

function renderPagination(container, { currentPage, totalPages }) {
  if (!container) return;

  if (!Number.isFinite(totalPages) || totalPages <= 1) {
    container.innerHTML = '';
    return;
  }

  const pagesMarkup = buildPageWindow(currentPage, totalPages)
    .map((item) => {
      if (item == null) return '<span class="deco">...</span>';
      if (item === currentPage) return `<span class="page current">${item}</span>`;
      return `<span class="page"><a href="${escapeHtml(
        buildHrefForPage(item)
      )}" data-layers-page="${item}">${item}</a></span>`;
    })
    .join('');

  const previousMarkup = currentPage > 1
    ? `<span class="prev"><a href="${escapeHtml(
        buildHrefForPage(currentPage - 1)
      )}" data-layers-page="${currentPage - 1}" aria-label="Previous">&larr;</a></span>`
    : '';

  const nextMarkup = currentPage < totalPages
    ? `<span class="next"><a href="${escapeHtml(
        buildHrefForPage(currentPage + 1)
      )}" data-layers-page="${currentPage + 1}" aria-label="Next">&rarr;</a></span>`
    : '';

  container.innerHTML = `<nav class="layers-pagination" aria-label="Pagination">${previousMarkup}${pagesMarkup}${nextMarkup}</nav>`;
}

function getFacetLabelByCode(code, definitionsByCode) {
  return definitionsByCode[code] || humanizeFacetCode(code);
}

function getFacetContentId(facetCode) {
  if (isColorFacet(facetCode)) {
    return 'layers-filter-content-color';
  }

  return `layers-filter-content-${getFacetCodeToken(facetCode)}`;
}

function getFacetOpenState(facetCode, selectedCount) {
  if (Object.prototype.hasOwnProperty.call(state.openFacetCodes, facetCode)) {
    return Boolean(state.openFacetCodes[facetCode]);
  }

  return selectedCount > 0;
}

function isSizeFacet(facetCode) {
  const code = String(facetCode || '');
  if (code === 'pf_t_size') return true;

  const token = toDomToken(code.split('.').pop());
  if (!token) return false;
  if (token === 'size') return true;
  if (token.endsWith('-size')) return true;
  if (token.indexOf('-size-') >= 0) return true;
  return false;
}

function normalizeSizeGroupCode(rawGroupCode) {
  const token = toDomToken(rawGroupCode);
  if (!token) return '';

  if (
    token === 'band' ||
    token === 'wired' ||
    token === 'wire' ||
    token === 'underwire' ||
    token === 'under-wire'
  ) {
    return 'band';
  }

  if (
    token === 'size' ||
    token === 'nonwire' ||
    token === 'non-wire' ||
    token === 'wireless' ||
    token === 'bralette'
  ) {
    return 'size';
  }

  if (token.startsWith('band')) return 'band';
  if (token.includes('non') && token.includes('wire')) return 'size';
  if (token.includes('wire')) return 'band';
  if (token.includes('size')) return 'size';
  return token;
}

function inferSizeGroupCode(label) {
  const raw = String(label || '').trim();
  if (!raw) return '';

  const token = toDomToken(raw);
  if (!token) return '';

  if (/^\d/.test(raw) || /\d/.test(token)) return 'band';

  if (
    /^(xxs|xs|s|m|l|xl|xxl|xxxl|1x|2x|3x|4x)$/.test(token) ||
    /^(xs|s|m|l|xl|xxl|xxxl)-(xs|s|m|l|xl|xxl|xxxl)$/.test(token)
  ) {
    return 'size';
  }

  return '';
}

function parseSizeFacetValue(value) {
  const raw = String(value || '').replace(/^boost:/, '').trim();
  if (!raw) return { groupCode: '', label: '' };

  const explicitGroupMatch = raw.match(/^([^:|>]+)\s*[:|>]\s*(.+)$/);
  if (explicitGroupMatch) {
    const groupCode = normalizeSizeGroupCode(explicitGroupMatch[1]);
    const label = String(explicitGroupMatch[2] || '').trim();
    return {
      groupCode,
      label: label || raw,
    };
  }

  const prefixedValueMatch = raw.match(/^(band|size|wired|non[-_\s]?wire|under[-_\s]?wire|wireless)[-_\s]+(.+)$/i);
  if (prefixedValueMatch) {
    const groupCode = normalizeSizeGroupCode(prefixedValueMatch[1]);
    const label = String(prefixedValueMatch[2] || '').trim();
    return {
      groupCode,
      label: label || raw,
    };
  }

  return {
    groupCode: inferSizeGroupCode(raw),
    label: raw,
  };
}

function getSizeHeaderLabel(groupCode) {
  const code = toDomToken(groupCode);
  if (!code) return '';
  if (SIZE_HEADER_LABELS[code]) return SIZE_HEADER_LABELS[code];
  return code;
}

function getSizeValueSortIndex(option) {
  const token = toDomToken(option?.label || option?.value || '');
  const index = SIZE_VALUE_ORDER.indexOf(token);
  return index >= 0 ? index : SIZE_VALUE_ORDER.length;
}

function getFacetCodeToken(facetCode) {
  const rawToken = toDomToken(String(facetCode || '').split('.').pop());
  if (!rawToken) return 'facet';

  const normalizedToken = rawToken.replace(/^pf-(?:t|p|vp)-/, '');
  return normalizedToken || rawToken;
}

function buildSizeOptionGroups(options) {
  const groupsByCode = {};
  const orderedCodes = [];

  options.forEach((option) => {
    const groupCode = option.groupCode || '';
    if (!groupsByCode[groupCode]) {
      groupsByCode[groupCode] = [];
      orderedCodes.push(groupCode);
    }
    groupsByCode[groupCode].push(option);
  });

  const getGroupPriority = (groupCode) => {
    if (groupCode === 'band') return 0;
    if (groupCode === 'size') return 1;
    if (!groupCode) return 99;
    return 50;
  };

  const orderedGroupCodes = [...orderedCodes].sort((a, b) => {
    const priorityDelta = getGroupPriority(a) - getGroupPriority(b);
    if (priorityDelta !== 0) return priorityDelta;
    return orderedCodes.indexOf(a) - orderedCodes.indexOf(b);
  });

  return orderedGroupCodes.map((groupCode) => ({
    code: groupCode,
    id: toDomToken(groupCode || 'size'),
    label: getSizeHeaderLabel(groupCode),
    options: [...groupsByCode[groupCode]].sort((a, b) => {
      const orderDelta = getSizeValueSortIndex(a) - getSizeValueSortIndex(b);
      if (orderDelta !== 0) return orderDelta;
      return groupsByCode[groupCode].indexOf(a) - groupsByCode[groupCode].indexOf(b);
    }),
  }));
}

function normalizeFacetOptionLabel(label) {
  const token = toDomToken(label);
  if (token) return token;
  return String(label || '').trim().toLowerCase();
}

function isNullFacetValue(value) {
  const normalized = String(value == null ? '' : value).trim().toLowerCase();
  return normalized === '' || normalized === 'null' || normalized === '(null)';
}

function isManualOrderFacetToken(token) {
  return MANUAL_ORDER_FACET_TOKENS.includes(String(token || ''));
}

function findFacetOptionHeaderIndex(options, optionLabels) {
  if (!Array.isArray(options) || !options.length) return -1;
  const normalizedLabels = new Set(optionLabels.map(normalizeFacetOptionLabel));

  return options.findIndex((option) => normalizedLabels.has(normalizeFacetOptionLabel(option.label)));
}

function buildHeaderOptionGroups(options, headerConfig) {
  const matchIndex = findFacetOptionHeaderIndex(options, headerConfig.optionLabels);
  if (matchIndex < 0) return [];

  if (matchIndex === 0) {
    return [
      {
        id: headerConfig.id,
        label: headerConfig.label,
        options,
      },
    ];
  }

  return [
    {
      id: headerConfig.beforeId || `${headerConfig.id}-before`,
      label: headerConfig.beforeLabel || '',
      options: options.slice(0, matchIndex),
    },
    {
      id: headerConfig.id,
      label: headerConfig.label,
      options: options.slice(matchIndex),
    },
  ];
}

function applyCustomFacetGroups(facets) {
  if (!Array.isArray(facets) || !facets.length) return facets;

  const cottonUnderwearConfig = FACET_HEADER_GROUPS.cottonUnderwear;
  const cottonUnderwearFacetToken = cottonUnderwearConfig.facetTokens.find((token) =>
    facets.some(
      (facet) =>
        facet.token === token &&
        findFacetOptionHeaderIndex(facet.options, cottonUnderwearConfig.optionLabels) >= 0
    )
  );

  return facets.map((facet) => {
    if (facet.optionGroups && facet.optionGroups.length) return facet;

    if (facet.token === 'bras') {
      const optionGroups = buildHeaderOptionGroups(facet.options, FACET_HEADER_GROUPS.braTops);
      if (optionGroups.length) return { ...facet, optionGroups };
      return facet;
    }

    if (facet.token === 'mixed') {
      const optionGroups = buildHeaderOptionGroups(facet.options, FACET_HEADER_GROUPS.everythingElse);
      if (optionGroups.length) return { ...facet, optionGroups };
      return facet;
    }

    if (cottonUnderwearFacetToken && facet.token === cottonUnderwearFacetToken) {
      const optionGroups = buildHeaderOptionGroups(facet.options, FACET_HEADER_GROUPS.cottonUnderwear);
      if (optionGroups.length) return { ...facet, optionGroups };
      return facet;
    }

    return facet;
  });
}

function mapLegacySwatchTokens(token) {
  const value = String(token || '');
  const has = (part) => value.indexOf(part) >= 0;

  if (has('black-buff-buff-black')) return ['black-buff-buff-black'];
  if (has('buff-black-buff-buff-white')) return ['buff-black-buff-buff-white'];
  if (has('black-buff-haze')) return ['black-buff-haze'];
  if (has('black-buff-coral')) return ['black-buff-coral'];
  if (has('black-buff-moon')) return ['black-buff-moon'];
  if (has('black-buff-peach-haze')) return ['black-buff-peach-haze'];
  if (has('black-buff-peach')) return ['black-buff-peach'];
  if (has('black-buff-slate')) return ['black-buff-slate'];
  if (has('black-buff-white')) return ['black-buff-white'];
  if (has('black-buff')) return ['black-buff'];
  if (has('black-essaouira')) return ['black-essaouira'];
  if (has('black-haze-moon')) return ['black-haze-moon'];
  if (has('black-marble')) return ['black-marble'];
  if (has('black-off-white') || has('black-offwhite')) return ['black-offwhite'];
  if (has('black-peach-slate')) return ['black-peach-slate'];
  if (has('black-slate')) return ['black-slate'];
  if (has('black-white-buff')) return ['black-white-buff'];
  if (has('black-white-moon')) return ['black-white-moon'];
  if (has('black-white-heather-grey')) return ['black-white-grey'];
  if (has('black-white')) return ['black-white'];

  if (has('buff-boa')) return ['buff-boa'];
  if (has('buff-blacktrim') || has('buff-black')) return ['buff-blacktrim', 'buff-black'];
  if (has('buff-coral')) return ['buff-coral'];
  if (has('buff-essaouira')) return ['buff-essaouira'];
  if (has('buff-haze-peach')) return ['buff-haze-peach'];
  if (has('cherry-buff-white')) return ['cherry-buff-white'];
  if (has('coral-buff-white')) return ['coral-buff-white'];
  if (has('buff-white')) return has('pack') ? ['buff-white-stripe'] : ['buff-whitetrim', 'buff-white'];
  if (has('buff-off-white') || has('buff-offwhite')) return ['buff-offwhite'];

  if (has('cobalt-glacier')) return ['cobalt-glacier'];

  if (has('coral-haze-moon')) return ['coral-haze-moon'];
  if (has('cherry-coral-peach')) return ['cherry-coral-peach'];
  if (has('coral-peach')) return ['coral-peach'];
  if (has('coral-white')) return ['coral-white'];

  if (has('glacier-stripe')) return ['glacier-stripe'];
  if (has('haze-blacktrim') || has('haze-black')) return ['haze-blacktrim'];
  if (has('heather-grey-white')) return ['heather-grey-white'];
  if (has('lilac-essaouira')) return ['lilac-essaouira'];

  if (has('moon-colorblock')) return ['moon-colorblock'];
  if (has('moon-metallic')) return ['moon-metallic'];
  if (has('moon-black')) return ['moon-black'];

  if (has('navy-white')) return ['navy-white'];
  if (has('navy-cosmos')) return ['navy-cosmos'];
  if (has('navy-tie-dye') || has('navy-tiedye')) return ['navy-tiedye'];
  if (has('navy-moon')) return has('pack') ? ['navy-moon-stripe'] : ['navy-moon'];
  if (has('navy-slate-peach')) return ['navy-slate-peach'];

  if (has('off-white-black') || has('offwhite-black')) return ['offwhite-black'];

  if (has('peach-essaouira')) return ['peach-essaouira'];
  if (has('peach-coral')) return ['peach-coral'];
  if (has('peach-blacktrim') || has('peach-black')) return ['peach-blacktrim'];
  if (has('peach-haze-slate')) return ['peach-haze-slate'];
  if (has('peach-haze-moon')) return ['peach-haze-moon'];
  if (has('peach-moire')) return ['peach-moire'];
  if (has('slate-moire')) return ['slate-moire'];

  if (has('white-cherries')) return ['white-cherries'];
  if (has('white-feel')) return ['white-feel-good', 'white-feel'];
  if (has('white-boa')) return ['white-boa'];
  if (has('white-cosmos')) return ['white-cosmos'];
  if (has('white-peach')) return ['white-peach'];
  if (has('white-heather-grey')) return ['white-heather-grey'];
  if (has('white-essaouira')) return ['white-essaouira'];

  return [value.split('-')[0]];
}

function buildSwatchTokens(option) {
  const valueToken = toDomToken(option.value);
  const labelToken = toDomToken(option.label);
  const valueTailToken = toDomToken(String(option.value || '').split(':').pop());
  const token = valueToken || labelToken;
  if (!token) return [];

  const tokens = [];
  const addToken = (nextToken) => {
    const normalized = toDomToken(nextToken);
    if (!normalized || tokens.includes(normalized)) return;
    tokens.push(normalized);
  };

  const addTokenWithAliases = (nextToken) => {
    addToken(nextToken);
    const aliases = SWATCH_TOKEN_ALIASES[nextToken] || [];
    aliases.forEach(addToken);
  };

  mapLegacySwatchTokens(token).forEach(addTokenWithAliases);
  addTokenWithAliases(token);

  if (valueTailToken && valueTailToken !== token) {
    mapLegacySwatchTokens(valueTailToken).forEach(addTokenWithAliases);
    addTokenWithAliases(valueTailToken);
  }

  if (labelToken && labelToken !== token) {
    mapLegacySwatchTokens(labelToken).forEach(addTokenWithAliases);
    addTokenWithAliases(labelToken);
  }

  if (token.endsWith('-black')) {
    addTokenWithAliases(`${token}trim`);
  }

  if (token.endsWith('-white')) {
    addTokenWithAliases(token.replace(/-white$/, '-whitetrim'));
  }

  const tokenParts = token.split('-');
  if (tokenParts.length === 2) {
    addTokenWithAliases(`${tokenParts[1]}-${tokenParts[0]}`);
  }

  return tokens;
}

function buildRenderableFacetData(data, activeFilters) {
  const facetDefinitions = getFacetDefinitions();
  const facetMap = getResultFacets(data);

  const labelsByCode = {};
  facetDefinitions.forEach((facet) => {
    labelsByCode[facet.code] = facet.label;
  });

  const orderedCodes = [
    ...facetDefinitions.map((facet) => facet.code),
    ...Object.keys(facetMap),
    ...Object.keys(activeFilters.listFilters),
  ].filter((code, index, source) => source.indexOf(code) === index);

  const facets = orderedCodes
    .map((code) => {
      const token = getFacetCodeToken(code);
      const facetLabel = getFacetLabelByCode(code, labelsByCode);
      const valueCounts = {};
      const rawCounts = facetMap[code] || {};

      Object.entries(rawCounts).forEach(([value, count]) => {
        valueCounts[value] = Number.isFinite(Number(count)) ? Number(count) : 0;
      });

      const registeredValues = registerFacetValues(code, Object.keys(valueCounts));
      const selectedValues = (activeFilters.listFilters[code] || []).filter(
        (value) => !isNullFacetValue(value)
      );
      const values = [...registeredValues, ...Object.keys(valueCounts), ...selectedValues].filter(
        (value, index, source) => source.indexOf(value) === index
      ).filter((value) => !isNullFacetValue(value));
      const isSize = isSizeFacet(code) || /\bsize\b/i.test(facetLabel);
      const preserveOptionOrder = isSize || isManualOrderFacetToken(token);

      const options = values
        .map((value) => {
          const sizeFacetValue = isSize ? parseSizeFacetValue(value) : null;
          const label = isSize
            ? sizeFacetValue.label
            : formatFacetValueLabel(code, value);

          return {
            value,
            label,
            count: valueCounts[value] || 0,
            selected: selectedValues.includes(value),
            disabled: !selectedValues.includes(value) && (valueCounts[value] || 0) <= 0,
            groupCode: isSize ? sizeFacetValue.groupCode : '',
          };
        })
        .filter((option) => option.label);

      if (!preserveOptionOrder) {
        options.sort((a, b) => {
          return a.label.localeCompare(b.label);
        });
      }

      if (!preserveOptionOrder && options.length > MAX_FACET_VALUES) {
        const visibleOptions = options.slice(0, MAX_FACET_VALUES);
        options.forEach((option) => {
          if (option.selected && !visibleOptions.some((visibleOption) => visibleOption.value === option.value)) {
            visibleOptions.push(option);
          }
        });
        options.length = 0;
        options.push(...visibleOptions);
      }

      if (!options.length) return null;
      const optionGroups = isSize ? buildSizeOptionGroups(options) : [];

      return {
        code,
        token,
        label: facetLabel,
        options,
        optionGroups,
        selectedCount: selectedValues.length,
        hasSwatches: isColorFacet(code),
      };
    })
    .filter(Boolean);

  return { facets: applyCustomFacetGroups(facets), labelsByCode };
}

function renderFacetOptionItem(facetCode, option, hasSwatches, { isMobile = false } = {}) {
  const selectedClass = option.selected ? ' selected' : '';
  const disabledClass = option.disabled ? ' disabled' : '';
  const swatchTokens = hasSwatches ? buildSwatchTokens(option) : [];
  const swatchClass = swatchTokens.length
    ? ` swatch ${swatchTokens.map((token) => `product-${token}`).join(' ')}`
    : '';
  const listItemClass = `layers-filter-option${selectedClass}${disabledClass}`;
  const buttonClass = isMobile
    ? `menu-drawer__menu-item link link--text list-menu__item focus-inset layers-filter-option-button${selectedClass}`
    : `layers-filter-option-button${selectedClass}`;
  const valueClass = isMobile
    ? 'menu-drawer__label layers-filter-option-value'
    : 'layers-filter-option-value';

  const buttonMarkup = `<button type="button" class="${buttonClass}" data-layers-facet-value data-layers-facet-property="${escapeHtml(
    facetCode
  )}" data-layers-facet-value-code="${escapeHtml(option.value)}" data-layers-facet-value-label="${escapeHtml(
    option.label
  )}" aria-pressed="${option.selected ? 'true' : 'false'}" ${option.disabled ? 'disabled aria-disabled="true"' : ''}>
<span class="${valueClass}" aria-labelledby="${escapeHtml(
    option.label
  )}">${escapeHtml(option.label)}</span>
</button>`;

  if (!hasSwatches) {
    return `<li class="${listItemClass}">
${buttonMarkup}
</li>`;
  }

  const swatchImageClass = `layers-filter-swatch${swatchClass}`;

  return `<li class="${listItemClass}">
<span class="${swatchImageClass}" data-color="${escapeHtml(
    option.label
  )}" title="${escapeHtml(option.label)}"></span>
${buttonMarkup}
</li>`;
}

function renderMobileFacetModal(contentMarkup) {
  return `<div class="layers-filter-modal-shell" data-layers-mobile-backdrop>
<div class="layers-filter-modal gradient menu-drawer motion-reduce" role="dialog" aria-modal="true" aria-labelledby="layers-filter-modal-title" tabindex="-1">
<div class="menu-drawer__inner-container layers-filter-modal__inner">
<button type="button" class="layers-filter-modal__close" data-layers-mobile-close aria-label="Close filters">Close</button>
<div class="menu-drawer__navigation-container layers-filter-modal__container">
<nav class="menu-drawer__navigation layers-filter-modal__body" aria-labelledby="layers-filter-modal-title">
<h2 id="layers-filter-modal-title" class="visually-hidden">Filter By</h2>
${contentMarkup}
</nav>
<div class="layers-filter-modal__footer">
<button type="button" class="layers-filter-modal__clear" data-layers-clear-all>Clear all</button>
<button type="button" class="layers-filter-modal__apply" data-layers-mobile-close>Done</button>
</div>
</div>
</div>
</div>
</div>`;
}

function renderFacetCaret() {
  return '<svg aria-hidden="true" focusable="false" class="icon" width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7L13 1" stroke="black"></path></svg>';
}

function renderBackToTopCaret() {
  return '<svg aria-hidden="true" focusable="false" class="icon" width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 7L7 1L13 7" stroke="currentColor"></path></svg>';
}

function renderFacetTree(facets, { isMobile }) {
  if (!facets.length) {
    return isMobile ? renderMobileFacetModal('') : '';
  }

  const treeContentClass = isMobile
    ? 'layers-filter-tree-content'
    : 'layers-filter-tree-content layers-filter-tree-content--desktop';

  const facetsMarkup = facets
    .map((facet) => {
      const isOpen = isMobile
        ? getFacetOpenState(facet.code, facet.selectedCount)
        : state.openFacetCodes[facet.code] === true;
      const optionGroupCount = facet.optionGroups
        ? facet.optionGroups.filter((group) => group.options && group.options.length).length
        : 0;
      const optionsMarkup = facet.optionGroups && facet.optionGroups.length
        ? facet.optionGroups
            .map((group) => {
              const showGroupHeader = optionGroupCount > 1 && group.label;
              const groupHeaderClass = isMobile
                ? 'menu-drawer__menu-item link link--text list-menu__item focus-inset filter-header'
                : 'filter-header';
              const groupLabelClass = isMobile ? 'menu-drawer__label' : 'layers-filter-group-label';
              const groupHeader = showGroupHeader
                ? `<div class="${groupHeaderClass}" id="${escapeHtml(
                    group.id
                  )}"><span class="${groupLabelClass}">[ ${escapeHtml(group.label)} ]</span></div>`
                : '';

              const groupOptions = group.options
                .map((option) => renderFacetOptionItem(facet.code, option, facet.hasSwatches, { isMobile }))
                .join('');

              return `${groupHeader}${groupOptions}`;
            })
            .join('')
        : facet.options
            .map((option) => renderFacetOptionItem(facet.code, option, facet.hasSwatches, { isMobile }))
            .join('');
      const mobileSwatchFacetClass = facet.hasSwatches ? ' layers-filter-accordion--swatch' : '';
      const mobileOptionListBaseClass = facet.hasSwatches
        ? 'layers-filter-option-list layers-filter-option-list--swatch'
        : `layers-filter-option-list layers-filter-option-list--multiple${
            isSizeFacet(facet.code) ? ' layers-filter-option-list--size' : ''
          }`;
      const mobileOptionsMarkup = facet.optionGroups && facet.optionGroups.length
        ? `<ul class="layers-filter-option-groups" role="list">${facet.optionGroups
            .map((group) => {
              const showGroupHeader = optionGroupCount > 1 && group.label;
              const groupHeaderClass = isMobile
                ? 'menu-drawer__menu-item link link--text list-menu__item focus-inset filter-header'
                : 'filter-header';
              const groupLabelClass = isMobile ? 'menu-drawer__label' : 'layers-filter-group-label';
              const groupHeader = showGroupHeader
                ? `<div class="${groupHeaderClass}" id="${escapeHtml(
                    group.id
                  )}"><span class="${groupLabelClass}">[ ${escapeHtml(group.label)} ]</span></div>`
                : '';
              const groupOptions = group.options
                .map((option) => renderFacetOptionItem(facet.code, option, facet.hasSwatches, { isMobile }))
                .join('');
              const groupListClass = `${mobileOptionListBaseClass}${
                showGroupHeader ? ' layers-filter-option-list--grouped' : ''
              }`;

              return `<li class="layers-filter-option-group">
${groupHeader}
<ul class="${groupListClass}" role="list">
${groupOptions}
</ul>
</li>`;
            })
            .join('')}</ul>`
        : `<ul class="${mobileOptionListBaseClass}" role="list">
${optionsMarkup}
</ul>`;

      const mobileFacetBodyMarkup = `<div class="menu-drawer__submenu has-submenu gradient motion-reduce menu-drawer__submenu-childmenu layers-filter-accordion__content" id="${escapeHtml(
        getFacetContentId(facet.code)
      )}">
<div class="layers-filter-accordion__content-inner">
${mobileOptionsMarkup}
</div>
</div>`;
      const desktopFacetBodyMarkup = `<div class="layers-filter-accordion__content layers-filter-accordion__content--desktop" id="${escapeHtml(
        getFacetContentId(facet.code)
      )}">
<div class="layers-filter-accordion__content-inner">
${mobileOptionsMarkup}
</div>
</div>`;
      const accordionItemClass = isMobile
        ? `navigation-image layers-filter-accordion${mobileSwatchFacetClass}`
        : `layers-filter-accordion layers-filter-accordion--desktop${mobileSwatchFacetClass}`;
      const summaryClass = isMobile
        ? `menu-drawer__menu-base menu-drawer__menu-item menu-drawer__menu-item-dropdown list-menu__item link link--text focus-inset layers-filter-accordion__summary${
            facet.selectedCount ? ' selected' : ''
          }${isOpen ? ' show' : ''}`
        : `layers-filter-accordion__summary layers-filter-accordion__summary--desktop${
            facet.selectedCount ? ' selected' : ''
          }${isOpen ? ' show' : ''}`;
      const labelClass = isMobile
        ? 'menu-drawer__label layers-filter-accordion__label'
        : 'layers-filter-accordion__label';

      return `<li class="${accordionItemClass}" data-layers-facet-code="${escapeHtml(facet.code)}" data-layers-open="${isOpen ? 'true' : 'false'}">
<div role="button" tabindex="0" class="${summaryClass}" ${
        isMobile ? 'data-layers-mobile-facet-toggle' : 'data-layers-facet-toggle'
      } data-layers-facet-code="${escapeHtml(facet.code)}" aria-expanded="${isOpen ? 'true' : 'false'}" aria-controls="${escapeHtml(
        getFacetContentId(facet.code)
      )}">
<span class="${labelClass}">
<span class="layers-filter-accordion__title">${escapeHtml(facet.label)}</span>
${renderFacetCaret()}
</span>
</div>
${isMobile ? mobileFacetBodyMarkup : desktopFacetBodyMarkup}
</li>`;
    })
    .join('');

  const contentMarkup = isMobile
    ? `<ul class="${treeContentClass} menu-drawer__menu has-submenu list-menu" role="list"><li class="navigation-image layers-filter-menu-spacer" aria-hidden="true"></li><li class="navigation-image layers-filter-menu-spacer" aria-hidden="true"></li>${facetsMarkup}</ul>`
    : `<ul class="${treeContentClass}" role="list">${facetsMarkup}</ul>`;

  return isMobile ? renderMobileFacetModal(contentMarkup) : contentMarkup;
}

function renderRefineBy(activeFilters, labelsByCode) {
  const items = [];

  Object.entries(activeFilters.listFilters).forEach(([property, values]) => {
    const facetLabel = getFacetLabelByCode(property, labelsByCode);
    const isSizeLikeFacet = isSizeFacet(property) || /\bsize\b/i.test(facetLabel);

    values.forEach((value) => {
      const valueLabel = isSizeLikeFacet
        ? parseSizeFacetValue(value).label
        : formatFacetValueLabel(property, value);

      items.push({
        property,
        value,
        label: `${facetLabel}: ${valueLabel}`,
      });
    });
  });

  Object.entries(activeFilters.rangeFilters).forEach(([property, range]) => {
    const hasMin = Number.isFinite(range.min);
    const hasMax = Number.isFinite(range.max);
    if (!hasMin && !hasMax) return;

    const valueLabel = hasMin && hasMax
      ? `${range.min} - ${range.max}`
      : hasMin
        ? `${range.min}+`
        : `Up to ${range.max}`;

    items.push({
      property,
      value: '',
      label: `${getFacetLabelByCode(property, labelsByCode)}: ${valueLabel}`,
    });
  });

  if (!items.length) return '';

  const itemsMarkup = items
    .map((item) => `<span class="layers-refine-by__item">
<a href="${escapeHtml(
      buildHrefForPage(1)
    )}" class="layers-refine-by__clear" data-layers-clear-filter data-layers-clear-property="${escapeHtml(
      item.property
    )}" ${item.value ? `data-layers-clear-value="${escapeHtml(item.value)}"` : ''}>${escapeHtml(
      item.label
    )}</a>
</span>`)
    .join('');

  return `<div class="layers-refine-by">
<span class="layers-refine-by__label">Refine by</span>
<div class="layers-refine-by__items">${itemsMarkup}</div>
<a href="${escapeHtml(
    buildHrefForPage(1)
  )}" class="layers-refine-by__clear-all" data-layers-clear-all>Clear all</a>
</div>`;
}

function getMobileFilterHost() {
  const mobileTree = document.querySelector('.layers-filter-tree-host');
  if (mobileTree && document.body && mobileTree.parentElement !== document.body) {
    document.body.appendChild(mobileTree);
  }

  return mobileTree;
}

function setMobileFiltersOpen(isOpen) {
  state.mobileFiltersOpen = Boolean(isOpen);

  if (state.mobileFiltersOpen) {
    state.openFacetCodes = {};
  }

  const mobileTree = getMobileFilterHost();
  if (mobileTree) {
    mobileTree.setAttribute('data-layers-mobile-open', state.mobileFiltersOpen ? 'true' : 'false');
    mobileTree.setAttribute('aria-hidden', state.mobileFiltersOpen ? 'false' : 'true');
  }

  document.body?.classList.toggle('layers-filter-modal-open', state.mobileFiltersOpen);
}

function renderFacetControls(data, activeFilters) {
  const desktopTreeContainer = document.querySelector('.layers-filter-tree-desktop-host');
  const mobileTreeContainer = getMobileFilterHost();
  const mobileButtonContainer = document.querySelector('.layers-filter-toggle-host');
  const refineByContainer = document.querySelector('.layers-refine-by-host');

  const { facets, labelsByCode } = buildRenderableFacetData(data, activeFilters);

  if (desktopTreeContainer) {
    desktopTreeContainer.innerHTML = renderFacetTree(facets, { isMobile: false });
  }

  if (mobileTreeContainer) {
    mobileTreeContainer.innerHTML = renderFacetTree(facets, { isMobile: true });
    mobileTreeContainer.setAttribute('data-layers-mobile-open', state.mobileFiltersOpen ? 'true' : 'false');
    mobileTreeContainer.setAttribute('aria-hidden', state.mobileFiltersOpen ? 'false' : 'true');
  }

  if (mobileButtonContainer) {
    const activeCount = getAllActiveFilterCount(activeFilters);
    mobileButtonContainer.innerHTML = `<button type="button" class="layers-filter-toggle-button" data-layers-mobile-filter-toggle>Filter${
      activeCount ? ` (${activeCount})` : ''
    }</button>`;
  }

  if (refineByContainer) {
    refineByContainer.innerHTML = renderRefineBy(activeFilters, labelsByCode);
  }
}

function renderFacetLoadingControls() {
  const desktopTreeContainer = document.querySelector('.layers-filter-tree-desktop-host');
  const mobileTreeContainer = getMobileFilterHost();
  const mobileButtonContainer = document.querySelector('.layers-filter-toggle-host');
  const refineByContainer = document.querySelector('.layers-refine-by-host');

  if (desktopTreeContainer) {
    desktopTreeContainer.innerHTML = renderFacetTreeSkeleton({ isMobile: false });
  }

  if (mobileTreeContainer && !state.mobileFiltersOpen) {
    mobileTreeContainer.innerHTML = renderFacetTreeSkeleton({ isMobile: true });
    mobileTreeContainer.setAttribute('data-layers-mobile-open', state.mobileFiltersOpen ? 'true' : 'false');
    mobileTreeContainer.setAttribute('aria-hidden', state.mobileFiltersOpen ? 'false' : 'true');
  }

  if (mobileButtonContainer) {
    mobileButtonContainer.innerHTML = renderFilterButtonSkeleton();
  }

  if (refineByContainer) {
    refineByContainer.innerHTML = '';
  }
}

function toggleMobileFacetAccordion(mobileFacetToggleButton) {
  const facetCode = String(mobileFacetToggleButton.getAttribute('data-layers-facet-code') || '');
  const facetOption = mobileFacetToggleButton.closest('.layers-filter-accordion');
  if (!facetCode || !facetOption) return;

  const nextOpen = mobileFacetToggleButton.getAttribute('aria-expanded') !== 'true';
  const content = facetOption.querySelector('.layers-filter-accordion__content');

  facetOption.setAttribute('data-layers-open', nextOpen ? 'true' : 'false');
  mobileFacetToggleButton.classList.toggle('show', nextOpen);
  mobileFacetToggleButton.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');

  if (content) {
    content.style.removeProperty('display');
    content.style.removeProperty('height');
    content.style.removeProperty('padding-top');
    content.style.removeProperty('padding-bottom');
  }

  state.openFacetCodes[facetCode] = nextOpen;
}

function getInfiniteObserverTarget(container) {
  if (!container) return null;

  let target = container.querySelector('[data-layers-load-more-sentinel]');
  if (!target) {
    target = document.createElement('span');
    target.setAttribute('data-layers-load-more-sentinel', '');
    target.setAttribute('aria-hidden', 'true');
    container.appendChild(target);
  }

  return target;
}

function clearInfiniteObserver() {
  if (!state.infiniteObserver) return;
  state.infiniteObserver.disconnect();
  state.infiniteObserver = null;
}

function loadNextPage() {
  const nextPage = state.lastPageMeta.currentPage + 1;
  if (nextPage > state.lastPageMeta.totalPages) return;
  if (state.isInfiniteLoading) return;

  state.isInfiniteLoading = true;

  const pageType = getPageType();
  const paginationMode = getPaginationMode(pageType);
  const loadMoreContainer = document.querySelector('.layers-load-more-host');
  if (loadMoreContainer) {
    renderLoadMore(loadMoreContainer, {
      currentPage: state.lastPageMeta.currentPage,
      totalPages: state.lastPageMeta.totalPages,
      paginationMode,
    });
  }

  updateLocationForPage(nextPage);
  runAsyncTask('Refresh Collection/Search (Load More)', refreshCollectionSearch);
}

function renderLoadMore(container, { currentPage, totalPages, paginationMode }) {
  if (!container) return;

  if (
    paginationMode === DEFAULT_PAGINATION_MODE ||
    !Number.isFinite(totalPages) ||
    totalPages <= 1 ||
    currentPage >= totalPages
  ) {
    container.innerHTML = '';
    clearInfiniteObserver();
    return;
  }

  container.setAttribute('data-layers-pagination-mode', paginationMode);
  container.innerHTML = `<button type="button" class="layers-load-more-button${
    state.isInfiniteLoading ? ' layers-load-more-button--loading' : ''
  }" data-layers-load-more ${
    state.isInfiniteLoading ? 'disabled aria-busy="true"' : ''
  }>${
    state.isInfiniteLoading
      ? '<span class="visually-hidden">Loading</span><span class="layers-load-more-spinner" aria-hidden="true"></span>'
      : 'Load more'
  }</button>`;

  if (paginationMode !== 'infinite' || !('IntersectionObserver' in window)) {
    clearInfiniteObserver();
    return;
  }

  const target = getInfiniteObserverTarget(container);
  if (!target) return;

  clearInfiniteObserver();
  state.infiniteObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          loadNextPage();
        }
      });
    },
    { rootMargin: '400px 0px 400px 0px' }
  );

  state.infiniteObserver.observe(target);
}

function renderPaginationControls({
  paginationContainer,
  loadMoreContainer,
  currentPage,
  totalPages,
  paginationMode,
}) {
  if (paginationMode === DEFAULT_PAGINATION_MODE) {
    renderPagination(paginationContainer, { currentPage, totalPages });
  } else if (paginationContainer) {
    paginationContainer.innerHTML = '';
  }

  renderLoadMore(loadMoreContainer, { currentPage, totalPages, paginationMode });
}

function appendProductsToGrid(resultsContainer, products, pageNumber) {
  const existingIds = new Set(
    Array.from(resultsContainer.querySelectorAll('[data-product-id]'))
      .map((node) => String(node.getAttribute('data-product-id') || '').trim())
      .filter(Boolean)
  );

  const html = products
    .filter((product) => {
      const id = getProductId(product);
      if (!id) return true;
      return !existingIds.has(id);
    })
    .map((product, index) => renderProductGridCard(product, pageNumber, getProductPositionForPageIndex(pageNumber, index)))
    .join('');

  if (!html) return;
  resultsContainer.insertAdjacentHTML('beforeend', html);
}

function scrollToResultPage(resultsContainer, page) {
  if (!resultsContainer || !Number.isFinite(page) || page <= 1) return;

  window.requestAnimationFrame(() => {
    const target = resultsContainer.querySelector(`[data-layers-result-page="${page}"]`);
    if (!target) return;

    target.scrollIntoView({ block: 'start' });
  });
}

function scrollToProductPosition(resultsContainer, position, { smooth = false } = {}) {
  if (!resultsContainer || !Number.isFinite(position) || position <= 0) return false;

  const target = resultsContainer.querySelector(`[data-layers-product-position="${position}"]`);
  if (!target) return false;

  state.isRestoringScrollPosition = true;
  window.requestAnimationFrame(() => {
    target.scrollIntoView({
      block: 'start',
      behavior: smooth && !prefersReducedViewMotion() ? 'smooth' : 'auto',
    });
    window.setTimeout(() => {
      state.isRestoringScrollPosition = false;
    }, smooth && !prefersReducedViewMotion() ? 900 : 500);
  });

  return true;
}

function hasRenderedResultPage(resultsContainer, page) {
  if (!resultsContainer || !Number.isFinite(page) || page <= 0) return false;
  return Boolean(resultsContainer.querySelector(`[data-layers-result-page="${page}"]`));
}

function getScrollTrackedProduct(resultsContainer) {
  if (!resultsContainer) return null;

  const headerOffset = document.querySelector('.shopify-section-header-sticky, sticky-header, .site-header')?.getBoundingClientRect().height || 0;
  const viewportAnchor = headerOffset + Math.min(window.innerHeight * 0.35, 260);
  const productSelector = '[data-layers-result-page][data-layers-product-position]';
  const xPoints = [
    Math.floor(window.innerWidth * 0.25),
    Math.floor(window.innerWidth * 0.5),
    Math.floor(window.innerWidth * 0.75),
  ];
  const yPoints = [
    viewportAnchor,
    viewportAnchor + 64,
    viewportAnchor + 128,
    viewportAnchor + 192,
  ];

  for (const yPoint of yPoints) {
    const y = Math.max(0, Math.min(window.innerHeight - 1, Math.floor(yPoint)));
    for (const xPoint of xPoints) {
      const x = Math.max(0, Math.min(window.innerWidth - 1, xPoint));
      const element = document.elementFromPoint(x, y);
      const product = element?.closest?.(productSelector);
      if (product && resultsContainer.contains(product)) return product;
    }
  }

  const visibleProducts = resultsContainer.querySelectorAll(productSelector);
  const maxFallbackChecks = Math.min(visibleProducts.length, 8);
  let bestProduct = null;
  let bestDistance = Infinity;
  const checkIndexes = new Set();

  for (let index = 0; index < maxFallbackChecks; index += 1) {
    checkIndexes.add(index);
  }

  for (let index = visibleProducts.length - maxFallbackChecks; index < visibleProducts.length; index += 1) {
    if (index >= 0) checkIndexes.add(index);
  }

  for (const index of checkIndexes) {
    const product = visibleProducts[index];
    const rect = product.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) continue;

    const distance = Math.abs(rect.top - viewportAnchor);
    if (distance < bestDistance) {
      bestProduct = product;
      bestDistance = distance;
    }
  }

  return bestProduct;
}

function replaceLocationForScrollPosition(page, productPosition) {
  const normalizedPage = Number(page);
  const normalizedPosition = Number(productPosition);
  if (!Number.isFinite(normalizedPage) || normalizedPage <= 0) return;
  if (!Number.isFinite(normalizedPosition) || normalizedPosition <= 0) return;

  const currentParams = new URLSearchParams(window.location.search);
  const currentPage = Number(currentParams.get('page') || 1);
  const currentPosition = Number(currentParams.get('product_position') || 0);

  if (normalizedPosition < MIN_SCROLL_TRACKED_PRODUCT_POSITION) {
    if (!currentPosition) return;

    replaceHistorySearchParams((params) => {
      params.delete('product_position');
      if (normalizedPage <= 1) {
        params.delete('page');
      }
    });
    return;
  }

  if (currentPage === normalizedPage && currentPosition === normalizedPosition) return;

  replaceHistorySearchParams((params) => {
    if (normalizedPage > 1) {
      params.set('page', String(normalizedPage));
    } else {
      params.delete('page');
    }
    params.set('product_position', String(normalizedPosition));
  });
}

function getSortControlHost() {
  const existingHost = document.querySelector('.layers-sort-host');
  if (existingHost) return existingHost;

  const toolbar = document.querySelector('.layers-filter-toolbar__row');
  if (!toolbar) return null;

  const host = document.createElement('div');
  host.className = 'layers-sort-host';
  toolbar.appendChild(host);
  return host;
}

function getSortingLabelText() {
  const labels = window.layersThemeConfig && window.layersThemeConfig.label;
  return (labels && labels.sorting) || 'Sort';
}

function renderSortControl(allowedScopes) {
  const host = getSortControlHost();
  if (!host) return;

  const sorts = getSortsForScope(allowedScopes);
  if (!sorts.length) {
    host.innerHTML = '';
    return;
  }

  const activeCode = resolveSortCode(allowedScopes);
  const optionsMarkup = sorts
    .map((sort) => `<option value="${escapeHtml(sort.code)}"${
      sort.code === activeCode ? ' selected' : ''
    }>${escapeHtml(sort.label)}</option>`)
    .join('');

  host.innerHTML = `<select class="layers-sort-select" aria-label="${escapeHtml(
    getSortingLabelText()
  )}" data-layers-sort>${optionsMarkup}</select>`;
}

async function fetchCollectionPage({ handle, page, limit, filters, sortOrderCode }) {
  const sdk = getSdk();
  return sdk.collection({
    handle,
    defaultSort: getConfig().defaultSortOrder || undefined,
  }).execute({
    page,
    limit,
    filters,
    sort: sortOrderCode || undefined,
    includeMeta: true,
  });
}

async function fetchSearchPage({ query, searchId, page, limit, filters, sortOrderCode }) {
  const sdk = getSdk();
  return sdk.search().execute({
    query,
    searchId: searchId || undefined,
    page,
    limit,
    filters,
    sort: sortOrderCode || undefined,
  });
}

async function refreshCollectionSearch() {
  const pageType = getPageType();
  if (pageType !== 'collection' && pageType !== 'search') return;

  const resultsContainer = document.querySelector('.layers-results');
  if (!resultsContainer) return;

  const allowedScopes = pageType === 'search' ? SCOPES.search : SCOPES.collection;
  renderSortControl(allowedScopes);

  const paginationContainer = document.querySelector('.layers-pagination-host');
  const loadMoreContainer = document.querySelector('.layers-load-more-host');

  const page = getCurrentPage();
  const productPosition = getCurrentProductPosition();
  const limit = getPageLimit(pageType);
  const filters = getFiltersFromLocation();
  const activeFilters = getActiveFilterStateFromLocation();
  const sortOrderCode = resolveSortCode(allowedScopes);
  const paginationMode = getPaginationMode(pageType);

  let queryKey = buildCollectionSearchQueryKey();
  const pageAlreadyRendered =
    paginationMode !== DEFAULT_PAGINATION_MODE &&
    page > 1 &&
    state.lastQueryKey === queryKey &&
    hasRenderedResultPage(resultsContainer, page);

  if (pageAlreadyRendered) {
    state.lastPageMeta = {
      currentPage: page,
      totalPages: state.lastPageMeta.totalPages,
    };
    renderPaginationControls({
      paginationContainer,
      loadMoreContainer,
      currentPage: page,
      totalPages: state.lastPageMeta.totalPages,
      paginationMode,
    });
    if (!productPosition || !scrollToProductPosition(resultsContainer, productPosition, { smooth: true })) {
      scrollToResultPage(resultsContainer, page);
    }
    return;
  }

  let shouldAppend =
    paginationMode !== DEFAULT_PAGINATION_MODE &&
    page > 1 &&
    state.lastQueryKey === queryKey;
  let shouldHydrateLeadingPages =
    paginationMode !== DEFAULT_PAGINATION_MODE &&
    page > 1 &&
    !shouldAppend;
  let searchIdForRequest = '';

  const requestId = state.requestId + 1;
  state.requestId = requestId;

  resultsContainer.setAttribute('aria-busy', 'true');
  if (shouldAppend) {
    resultsContainer.insertAdjacentHTML(
      'beforeend',
      renderProductGridSkeletons(Math.max(3, Math.min(6, limit)))
    );
    renderLoadMore(loadMoreContainer, {
      currentPage: state.lastPageMeta.currentPage,
      totalPages: state.lastPageMeta.totalPages,
      paginationMode,
    });
  } else {
    resultsContainer.innerHTML = renderProductGridSkeletons(getProductGridSkeletonCount(resultsContainer, limit));
    renderFacetLoadingControls();
    if (paginationContainer) paginationContainer.innerHTML = '';
    if (loadMoreContainer) loadMoreContainer.innerHTML = '';
  }

  let response;
  try {
    if (pageType === 'collection') {
      const handle = getCollectionHandle();
      if (!handle) {
        resultsContainer.innerHTML = renderEmptyResults('Collection handle is missing.');
        renderFacetControls({ facets: {} }, activeFilters);
        renderPaginationControls({
          paginationContainer,
          loadMoreContainer,
          currentPage: 1,
          totalPages: 1,
          paginationMode,
        });
        resultsContainer.removeAttribute('aria-busy');
        return;
      }

      response = await fetchCollectionPage({
        handle,
        page,
        limit,
        filters,
        sortOrderCode,
      });
    } else {
      const query = getSearchQuery();
      if (!query) {
        resultsContainer.innerHTML = renderEmptyResults('Enter a search term.');
        renderFacetControls({ facets: {} }, activeFilters);
        renderPaginationControls({
          paginationContainer,
          loadMoreContainer,
          currentPage: 1,
          totalPages: 1,
          paginationMode,
        });
        resultsContainer.removeAttribute('aria-busy');
        return;
      }

      const searchId = await ensureSearchId(query, getSearchId());
      searchIdForRequest = searchId;
      queryKey = buildCollectionSearchQueryKey();
      shouldAppend =
        paginationMode !== DEFAULT_PAGINATION_MODE &&
        page > 1 &&
        state.lastQueryKey === queryKey;
      shouldHydrateLeadingPages =
        paginationMode !== DEFAULT_PAGINATION_MODE &&
        page > 1 &&
        !shouldAppend;

      response = await fetchSearchPage({
        query,
        searchId,
        page,
        limit,
        filters,
        sortOrderCode,
      });
    }
  } catch (error) {
    console.error('[Layers] Failed to fetch collection/search response:', error);

    if (requestId !== state.requestId) {
      if (shouldAppend) {
        removeProductGridSkeletons(resultsContainer);
      }
      return;
    }

    removeProductGridSkeletons(resultsContainer);
    if (!shouldAppend) {
      resultsContainer.innerHTML = renderEmptyResults('Unable to load results right now.');
    }

    state.isInfiniteLoading = false;
    renderFacetControls({ facets: {} }, activeFilters);
    renderPaginationControls({
      paginationContainer,
      loadMoreContainer,
      currentPage: page,
      totalPages: 1,
      paginationMode,
    });

    resultsContainer.removeAttribute('aria-busy');
    return;
  }

  if (requestId !== state.requestId) {
    if (shouldAppend) {
      removeProductGridSkeletons(resultsContainer);
    }
    return;
  }

  resultsContainer.removeAttribute('aria-busy');
  state.isInfiniteLoading = false;

  if (!response || response.error) {
    if (!shouldAppend) {
      resultsContainer.innerHTML = renderEmptyResults('Unable to load results right now.');
    }

    renderFacetControls({ facets: {} }, activeFilters);
    renderPaginationControls({
      paginationContainer,
      loadMoreContainer,
      currentPage: page,
      totalPages: 1,
      paginationMode,
    });
    return;
  }

  const data = response.data;
  const products = getResultProducts(data);

  const { totalResults, totalPages } = getResultPagination(data, {
    page,
    limit,
    fallbackResults: products.length,
  });
  const hydrateTargetPage = Math.min(page, totalPages);
  let hydratedPageResults = shouldHydrateLeadingPages && hydrateTargetPage > 1
    ? [{ page, products }]
    : null;

  if (hydratedPageResults) {
    const fetchHydratedPage = (pageNumber) => {
      if (pageType === 'collection') {
        return fetchCollectionPage({
          handle: getCollectionHandle(),
          page: pageNumber,
          limit,
          filters,
          sortOrderCode,
        });
      }

      return fetchSearchPage({
        query: getSearchQuery(),
        searchId: searchIdForRequest || getSearchId(),
        page: pageNumber,
        limit,
        filters,
        sortOrderCode,
      });
    };

    try {
      const priorPageResults = await Promise.all(
        Array.from({ length: hydrateTargetPage - 1 }, (_, index) => {
          const pageNumber = index + 1;
          return fetchHydratedPage(pageNumber).then((pageResponse) => ({
            page: pageNumber,
            response: pageResponse,
          }));
        })
      );

      if (requestId !== state.requestId) {
        return;
      }

      hydratedPageResults = priorPageResults
        .filter(({ response: pageResponse }) => pageResponse && !pageResponse.error)
        .map(({ page: pageNumber, response: pageResponse }) => ({
          page: pageNumber,
          products: getResultProducts(pageResponse.data),
        }))
        .concat({ page, products })
        .filter((pageResult) => pageResult.page <= hydrateTargetPage)
        .sort((a, b) => a.page - b.page);
    } catch (error) {
      console.error('[Layers] Failed to hydrate previous result pages:', error);
      hydratedPageResults = null;
    }
  }

  const renderResolvedState = () => {
    if (shouldAppend) {
      appendProductsToGrid(resultsContainer, products, page);
      removeProductGridSkeletons(resultsContainer);
    } else if (hydratedPageResults) {
      resultsContainer.innerHTML = hydratedPageResults
        .map((pageResult) => renderProductGridCards(pageResult.products, pageResult.page))
        .join('');
    } else if (!products.length) {
      resultsContainer.innerHTML = renderEmptyResults(
        pageType === 'search'
          ? `No results found for "${getSearchQuery()}".`
          : 'No products found for this collection.'
      );
    } else {
      resultsContainer.innerHTML = renderProductGridCards(products, page);
    }

    insertCollectionPosters(resultsContainer);

    state.lastPageMeta = { currentPage: hydrateTargetPage, totalPages };
    state.lastQueryKey = queryKey;

    renderFacetControls(data, activeFilters);
    renderPaginationControls({
      paginationContainer,
      loadMoreContainer,
      currentPage: page,
      totalPages,
      paginationMode,
    });
  };

  await scheduleLayersRender(renderResolvedState, !shouldAppend);

  if (hydratedPageResults) {
    if (productPosition && scrollToProductPosition(resultsContainer, productPosition, { smooth: true })) {
      syncScrollPositionUrl();
    } else {
      scrollToResultPage(resultsContainer, hydrateTargetPage);
    }
  } else if (!shouldAppend && productPosition) {
    scrollToProductPosition(resultsContainer, productPosition, { smooth: true });
  }

  logDebug('Collection/Search response', {
    pageType,
    query: pageType === 'search' ? getSearchQuery() : undefined,
    searchId: pageType === 'search' ? getSearchId() : undefined,
    page,
    limit,
    sortOrderCode,
    paginationMode,
    totalResults,
    totalPages,
    filters,
  });
}

function onCollectionSearchClick(event) {
  if (!(event.target instanceof Element)) return;
  const target = event.target;

  if (target.matches('[data-layers-mobile-backdrop]')) {
    event.preventDefault();
    setMobileFiltersOpen(false);
    return;
  }

  const mobileToggleButton = target.closest('[data-layers-mobile-filter-toggle]');
  if (mobileToggleButton) {
    event.preventDefault();
    setMobileFiltersOpen(!state.mobileFiltersOpen);
    return;
  }

  const mobileCloseButton = target.closest('[data-layers-mobile-close]');
  if (mobileCloseButton) {
    event.preventDefault();
    setMobileFiltersOpen(false);
    return;
  }

  const mobileFacetToggleButton = target.closest('[data-layers-mobile-facet-toggle]');
  if (mobileFacetToggleButton) {
    event.preventDefault();
    toggleMobileFacetAccordion(mobileFacetToggleButton);
    return;
  }

  const loadMoreButton = target.closest('[data-layers-load-more]');
  if (loadMoreButton) {
    event.preventDefault();
    loadNextPage();
    return;
  }

  const paginationLink = target.closest('a[data-layers-page]');
  if (paginationLink && paginationLink.closest('.layers-pagination-host')) {
    event.preventDefault();

    const page = Number(paginationLink.getAttribute('data-layers-page'));
    if (!Number.isFinite(page) || page < 1) return;

    state.isInfiniteLoading = false;
    updateLocationForPage(page);
    runAsyncTask('Refresh Collection/Search (Pagination)', refreshCollectionSearch);
    return;
  }

  const facetToggleButton = target.closest('[data-layers-facet-toggle]');
  if (facetToggleButton) {
    event.preventDefault();

    const facetCode = String(facetToggleButton.getAttribute('data-layers-facet-code') || '');
    if (!facetCode) return;
    toggleMobileFacetAccordion(facetToggleButton);
    return;
  }

  const facetValueButton = target.closest('[data-layers-facet-value]');
  if (facetValueButton) {
    event.preventDefault();
    if (facetValueButton.hasAttribute('disabled')) return;

    const property = String(facetValueButton.getAttribute('data-layers-facet-property') || '');
    const valueCode = String(facetValueButton.getAttribute('data-layers-facet-value-code') || '');
    if (!property || !valueCode) return;

    state.isInfiniteLoading = false;
    updateLocationForFacetToggle(property, valueCode);
    runAsyncTask('Refresh Collection/Search (Facet)', refreshCollectionSearch);
    return;
  }

  const clearFilterButton = target.closest('[data-layers-clear-filter]');
  if (clearFilterButton) {
    event.preventDefault();

    const property = String(clearFilterButton.getAttribute('data-layers-clear-property') || '');
    const value = String(clearFilterButton.getAttribute('data-layers-clear-value') || '');
    if (!property) return;

    const activeFilters = getActiveFilterStateFromLocation();
    const currentValues = activeFilters.listFilters[property] || [];

    updateLocationForFacet(
      property,
      value ? currentValues.filter((currentValue) => currentValue !== value) : []
    );

    state.isInfiniteLoading = false;
    runAsyncTask('Refresh Collection/Search (Clear Filter)', refreshCollectionSearch);
    return;
  }

  const clearAllButton = target.closest('[data-layers-clear-all]');
  if (clearAllButton) {
    event.preventDefault();
    state.isInfiniteLoading = false;
    clearAllFiltersFromLocation();
    runAsyncTask('Refresh Collection/Search (Clear All)', refreshCollectionSearch);
  }
}

function onMobileFacetToggleCapture(event) {
  if (!(event.target instanceof Element)) return;

  const facetToggleButton = event.target.closest('[data-layers-mobile-facet-toggle], [data-layers-facet-toggle]');
  if (!facetToggleButton) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
  toggleMobileFacetAccordion(facetToggleButton);
}

function onSortChange(event) {
  if (!(event.target instanceof Element)) return;
  const target = event.target;
  if (!target.matches('select[data-layers-sort]')) return;

  updateLocationForSort(String(target.value || '').trim());
  state.isInfiniteLoading = false;
  runAsyncTask('Refresh Collection/Search (Sort)', refreshCollectionSearch);
}

function onHistoryChange() {
  state.isInfiniteLoading = false;
  setMobileFiltersOpen(false);
  runAsyncTask('Refresh Collection/Search (History)', refreshCollectionSearch);
}

function syncBackToTopButton() {
  const button = document.querySelector('[data-layers-back-to-top]');
  if (!button) return;

  button.classList.toggle('is-visible', window.scrollY > 480);
}

function onBackToTopScroll() {
  if (state.backToTopTicking) return;

  state.backToTopTicking = true;
  window.requestAnimationFrame(() => {
    syncBackToTopButton();
    state.backToTopTicking = false;
  });
}

function syncScrollPositionUrl() {
  if (state.isRestoringScrollPosition) return;

  const pageType = getPageType();
  if (pageType !== 'collection' && pageType !== 'search') return;

  const resultsContainer = document.querySelector('.layers-results');
  const product = getScrollTrackedProduct(resultsContainer);
  if (!product) return;

  const page = Number(product.getAttribute('data-layers-result-page'));
  const productPosition = Number(product.getAttribute('data-layers-product-position'));
  replaceLocationForScrollPosition(page, productPosition);
}

function onScrollPositionUpdate() {
  if ('onscrollend' in window) return;

  if (state.scrollPositionUpdateTimer) {
    window.clearTimeout(state.scrollPositionUpdateTimer);
  }

  state.scrollPositionUpdateTimer = window.setTimeout(() => {
    state.scrollPositionUpdateTimer = null;
    syncScrollPositionUrl();
  }, 160);
}

function onBackToTopClick(event) {
  const button = event.target instanceof Element
    ? event.target.closest('[data-layers-back-to-top]')
    : null;
  if (!button) return;

  event.preventDefault();
  window.scrollTo({
    top: 0,
    behavior: prefersReducedViewMotion() ? 'auto' : 'smooth',
  });
}

function mountBackToTopButton() {
  if (document.querySelector('[data-layers-back-to-top]')) {
    syncBackToTopButton();
    return;
  }

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'layers-back-to-top';
  button.setAttribute('data-layers-back-to-top', '');
  button.setAttribute('aria-label', 'Back to top');
  button.innerHTML = renderBackToTopCaret();

  document.body.appendChild(button);
  syncBackToTopButton();
}

function bindRuntimeListeners() {
  if (listenersBound) return;

  document.addEventListener('click', onMobileFacetToggleCapture, true);
  document.addEventListener('click', onCollectionSearchClick);
  document.addEventListener('click', onBackToTopClick);
  document.addEventListener('change', onSortChange);
  window.addEventListener('popstate', onHistoryChange);
  window.addEventListener('scroll', onBackToTopScroll, { passive: true });
  window.addEventListener('scroll', onScrollPositionUpdate, { passive: true });
  window.addEventListener('scrollend', syncScrollPositionUrl, { passive: true });

  listenersBound = true;
}

async function mountCollectionSearch() {
  const pageType = getPageType();
  if (pageType !== 'collection' && pageType !== 'search') return;

  bindRuntimeListeners();
  setMobileFiltersOpen(false);
  mountBackToTopButton();

  await refreshCollectionSearch();
}




Object.assign(exports, { refreshCollectionSearch: refreshCollectionSearch, mountCollectionSearch: mountCollectionSearch });
})(__layersModules["./layers-collection-search.js"]);


__layersModules["./layers-recommendations.js"] = {};
((exports) => {
const { getConfig, getSdk, getResultProducts, logDebug, escapeHtml, getProductId, getProductTitle, getProductUrl, getProductImageUrl, renderResponsiveImage, renderProductImagePlaceholder, renderPriceMarkup } = __layersModules["./layers-shared.js"];


const DEFAULT_LIMITS = {
  productRecommendations: 8,
  completeTheSet: 4,
  cartRecommendations: 6,
};

function getCurrentProductHandle() {
  const pathMatch = String(window.location.pathname || '').match(/^\/products\/([^/?#]+)/);
  if (pathMatch?.[1]) return decodeURIComponent(pathMatch[1]);

  return window.meta?.product?.handle || window.ShopifyAnalytics?.meta?.product?.handle || '';
}

function getCurrentProductId() {
  return String(
    window.meta?.product?.id || window.ShopifyAnalytics?.meta?.product?.id || window.__st?.rid || ''
  ).trim();
}

function parseProductIdFromRecommendationElement(element) {
  const dataUrl = String(element?.getAttribute('data-url') || '').trim();
  if (!dataUrl) return '';

  try {
    return String(new URL(dataUrl, window.location.origin).searchParams.get('product_id') || '').trim();
  } catch {
    return '';
  }
}

function getBlockConfig(blocks, key) {
  const fallbackLimit = DEFAULT_LIMITS[key];
  const config = blocks[key] || {};

  return {
    blockId: String(config.blockId || '').trim(),
    limit: Number(config.limit) > 0 ? Math.floor(Number(config.limit)) : fallbackLimit,
  };
}

function getRecommendationsConfig() {
  const blocks = getConfig().blocks || {};

  return {
    productRecommendations: getBlockConfig(blocks, 'productRecommendations'),
    completeTheSet: getBlockConfig(blocks, 'completeTheSet'),
    cartRecommendations: getBlockConfig(blocks, 'cartRecommendations'),
  };
}

async function fetchBlockProducts({ blockId, anchorId, anchorHandle, limit }) {
  if (!blockId) return [];

  const sdk = getSdk();
  const anchor = anchorId || anchorHandle || undefined;
  const response = await sdk
    .blocks({
      blockId,
      anchor,
    })
    .execute({ limit });

  if (response.error) {
    console.error('[Layers] Recommendations request failed:', response.error);
    return [];
  }

  return getResultProducts(response.data);
}

function renderRecommendationCard(product) {
  const title = getProductTitle(product);
  const url = getProductUrl(product);
  const imageUrl = getProductImageUrl(product);

  return `<li class="grid__item">
<a href="${escapeHtml(url)}" class="full-unstyled-link">
<div class="card-wrapper product-card-wrapper">
<div class="card card--standard card--text">
<div class="card__inner color-background-2 gradient ratio layers-recommendation-ratio">
<div class="card__media">
<div class="media media--transparent">
${
  imageUrl
    ? renderResponsiveImage(imageUrl, title, 'motion-reduce')
    : renderProductImagePlaceholder()
}
</div>
</div>
</div>
<div class="card__content">
<div class="card__information">
<h3 class="card__heading h5">${escapeHtml(title)}</h3>
<div class="price">${renderPriceMarkup(product)}</div>
</div>
</div>
</div>
</div>
</a>
</li>`;
}

function renderMatchingSetCard(product) {
  const title = getProductTitle(product);
  const url = getProductUrl(product);
  const imageUrl = getProductImageUrl(product);

  return `<div class="matching-set__item product-grid-matching" data-product-id="${escapeHtml(
    getProductId(product)
  )}">
<div class="matching-set__image">
<a href="${escapeHtml(url)}" class="matching-set__title">
${imageUrl ? renderResponsiveImage(imageUrl, title, '') : renderProductImagePlaceholder()}
</a>
</div>
<div class="product-info">
<div class="matching-set__info">
<a href="${escapeHtml(url)}" class="matching-set__title">${escapeHtml(title)}</a>
<div class="matching-set__price"><span class="price-item">${renderPriceMarkup(product)}</span></div>
</div>
<div class="matching-set__buttons">
<a class="button button--full-width button--primary" href="${escapeHtml(url)}">View product</a>
</div>
</div>
</div>`;
}

function renderCartRecommendationCard(product, useThirds) {
  return `<div class="${useThirds ? 'product-wrapper one-third' : 'product-wrapper'}">${renderMatchingSetCard(
    product
  )}</div>`;
}

async function mountProductRecommendations(blockConfig) {
  if (!blockConfig.blockId) return;

  window.NEGATIVE_DISABLE_SHOPIFY_RECOMMENDATIONS = true;

  const targets = Array.from(document.querySelectorAll('product-recommendations'));
  if (!targets.length) return;

  await Promise.all(
    targets.map(async (target) => {
      const anchorId = parseProductIdFromRecommendationElement(target) || getCurrentProductId();
      const anchorHandle = getCurrentProductHandle();

      const products = await fetchBlockProducts({
        blockId: blockConfig.blockId,
        anchorId,
        anchorHandle,
        limit: blockConfig.limit,
      });

      if (!products.length) {
        target.innerHTML = '';
        target.setAttribute('hidden', 'hidden');
        return;
      }

      const existingHeading = target.querySelector('h1, h2, h3, .accordion__title');
      const headingMarkup = existingHeading ? existingHeading.outerHTML : '';
      const cardsMarkup = products.map((product) => renderRecommendationCard(product)).join('');

      target.removeAttribute('hidden');
      target.classList.add('product-recommendations--loaded');
      target.innerHTML = `${headingMarkup}<ul class="grid product-grid grid--4-col-desktop grid--2-col-tablet-down" role="list">${cardsMarkup}</ul>`;
    })
  );
}

async function mountCompleteTheSetRecommendations(blockConfig) {
  if (!blockConfig.blockId) return;

  const targets = Array.from(document.querySelectorAll('.matching-set'));
  if (!targets.length) return;

  const products = await fetchBlockProducts({
    blockId: blockConfig.blockId,
    anchorId: getCurrentProductId(),
    anchorHandle: getCurrentProductHandle(),
    limit: blockConfig.limit,
  });

  if (!products.length) return;

  targets.forEach((target) => {
    const container = target.querySelector('.matching-set__container');
    if (!container) return;

    container.innerHTML = products
      .map(
        (product, index) =>
          `${renderMatchingSetCard(product)}${
            index < products.length - 1 ? '<div class="matching-set__divider"></div>' : ''
          }`
      )
      .join('');
  });
}

async function mountCartRecommendations(blockConfig) {
  if (!blockConfig.blockId) return;

  const targets = Array.from(document.querySelectorAll('.cart-feature_collection'));
  if (!targets.length) return;

  const products = await fetchBlockProducts({
    blockId: blockConfig.blockId,
    anchorId: '',
    anchorHandle: '',
    limit: blockConfig.limit,
  });

  if (!products.length) return;

  targets.forEach((target) => {
    const wrapper = target.querySelector('.feature_collection-wrapper');
    if (!wrapper) return;

    const useThirds = products.length <= 3;
    wrapper.innerHTML = products
      .map((product) => renderCartRecommendationCard(product, useThirds))
      .join('');
  });
}

async function mountRecommendations() {
  const config = getRecommendationsConfig();
  await Promise.all([
    mountProductRecommendations(config.productRecommendations),
    mountCompleteTheSetRecommendations(config.completeTheSet),
    mountCartRecommendations(config.cartRecommendations),
  ]);

  logDebug('Recommendations mounted', config);
}




Object.assign(exports, { mountRecommendations: mountRecommendations });
})(__layersModules["./layers-recommendations.js"]);


__layersModules["./layers-featured-collection.js"] = {};
((exports) => {

const FEATURED_COLLECTION_BOUND_FLAG = '__NEGATIVE_FEATURED_COLLECTION_SLICK_BOUND__';

function getFeaturedCollectionSliders(root = document) {
  if (!root) return [];

  if (root instanceof Element && root.matches('[data-layers-featured-collection]')) {
    return [root];
  }

  if (typeof root.querySelectorAll === 'function') {
    return Array.from(root.querySelectorAll('[data-layers-featured-collection]'));
  }

  return Array.from(document.querySelectorAll('[data-layers-featured-collection]'));
}

function getSlickFactory() {
  const jq = window.jQuery || window.$;
  if (!jq || !jq.fn || typeof jq.fn.slick !== 'function') return null;
  return jq;
}

function initFeaturedCollectionSlider(slider) {
  if (!slider || !slider.isConnected) return true;
  if (!slider.children.length) return true;

  const jq = getSlickFactory();
  if (!jq) return false;

  const mobileColumns = Number(slider.dataset.layersMobileColumns || '2');
  const mobileSlides = Number.isFinite(mobileColumns) && mobileColumns > 0 ? Math.floor(mobileColumns) : 2;
  const $slider = jq(slider);

  if ($slider.hasClass('slick-initialized')) {
    $slider.slick('setPosition');
    return true;
  }

  $slider.slick({
    slidesToShow: 5,
    slidesToScroll: 5,
    centerMode: false,
    variableWidth: false,
    arrows: false,
    dots: false,
    infinite: true,
    touchThreshold: 10,
    responsive: [
      {
        breakpoint: 750,
        settings: {
          slidesToShow: mobileSlides,
          slidesToScroll: 1,
        },
      },
    ],
  });

  return true;
}

function initFeaturedCollectionSliders(root = document) {
  const sliders = getFeaturedCollectionSliders(root);
  if (!sliders.length) return;

  let hasPending = false;

  sliders.forEach((slider) => {
    try {
      const initialized = initFeaturedCollectionSlider(slider);
      if (!initialized) hasPending = true;
    } catch (error) {
      hasPending = true;
      console.warn('[Layers] Featured collection slider init failed.', error);
    }
  });

  if (hasPending) {
    window.setTimeout(() => initFeaturedCollectionSliders(root), 300);
    window.addEventListener('load', () => initFeaturedCollectionSliders(root), { once: true });
  }
}

function mountFeaturedCollectionSlick() {
  window.mountNegativeLayersFeaturedCollections = initFeaturedCollectionSliders;
  window.NegativeLayersComponents = window.NegativeLayersComponents || {};
  window.NegativeLayersComponents.mountFeaturedCollections = initFeaturedCollectionSliders;

  if (window[FEATURED_COLLECTION_BOUND_FLAG]) return;
  window[FEATURED_COLLECTION_BOUND_FLAG] = true;

  document.addEventListener('negative:layers:featured-collection:init', (event) => {
    const root = event.target instanceof Element ? event.target : document;
    initFeaturedCollectionSliders(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initFeaturedCollectionSliders(document), { once: true });
  } else {
    initFeaturedCollectionSliders(document);
  }
}


Object.assign(exports, { initFeaturedCollectionSliders: initFeaturedCollectionSliders, mountFeaturedCollectionSlick: mountFeaturedCollectionSlick });
})(__layersModules["./layers-featured-collection.js"]);


__layersModules["./layers-components-entry.js"] = {};
((exports) => {
const { mountAutocomplete } = __layersModules["./layers-autocomplete.js"];
const { mountCollectionSearch } = __layersModules["./layers-collection-search.js"];
const { mountFeaturedCollectionSlick } = __layersModules["./layers-featured-collection.js"];
const { mountRecommendations } = __layersModules["./layers-recommendations.js"];





async function mountLayersComponents() {
  try {
    mountFeaturedCollectionSlick();
    await mountCollectionSearch();
    await mountRecommendations();
    await mountAutocomplete();
    document.dispatchEvent(new CustomEvent('negative:layers:collection-search:init'));
  } catch (error) {
    console.error('[Layers] Failed to mount Layers components:', error);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountLayersComponents, { once: true });
} else {
  mountLayersComponents();
}


})(__layersModules["./layers-components-entry.js"]);
